/**
 * LiveOperations.jsx — Tax Flow UI 2.0 Primary Landing Page
 *
 * View modes (management only — staff are already server-scoped):
 *   All      → firm-wide view; optional assignee filter
 *   My Work  → items explicitly assigned to the logged-in user
 *
 * Client-side filters (all roles):
 *   Module   → All / Messages / Appointments / Webchat / Zoom
 *   Status   → All / Staff Action (orange) / Urgent (red) / Info (blue)
 *
 * All selected filter states are persisted in localStorage under
 * "liveops_prefs_v1" and restored on return.  Invalid stored values
 * (e.g. deleted staff ID) fall back to their permitted defaults.
 *
 * Cards remain visible until their underlying canonical workflow action
 * is truly completed — changing filters never marks a card complete.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity, MessageSquare, CalendarClock, MessageSquareWarning,
  Video, RefreshCw, CheckCircle2, Clock, Users, Zap,
  ArrowRight, User, Loader2, ChevronDown, AlertTriangle,
  MoreHorizontal, Archive, X, Briefcase, CreditCard, Calendar,
  BookOpen, ClipboardList, Globe, Trash2, AlertCircle, Send, ShieldCheck,
  Search, Filter, Database, AlertOctagon, RotateCcw, ChevronUp, CheckSquare,
  XCircle, Bell,
} from "lucide-react";
import { api } from "@/lib/api";
import {
  getLifecycleQueueHealth,
  getOperationalIncidents,
  acknowledgeIncident,
  retryIncident,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

// ── Persistence ────────────────────────────────────────────────────────────
const LS_KEY = "liveops_prefs_v1";
function loadPrefs() {
  try { return JSON.parse(localStorage.getItem(LS_KEY)) || {}; } catch { return {}; }
}
function savePrefs(p) {
  localStorage.setItem(LS_KEY, JSON.stringify(p));
}

// ── Status system ──────────────────────────────────────────────────────────
const STATUS = {
  orange: { border: "border-l-amber-500",  bg: "bg-amber-50",   dot: "bg-amber-500",  text: "text-amber-700",  ring: "ring-amber-200",  label: "Staff Action" },
  red:    { border: "border-l-red-500",    bg: "bg-red-50",     dot: "bg-red-500",    text: "text-red-700",    ring: "ring-red-200",    label: "Urgent" },
  blue:   { border: "border-l-blue-500",   bg: "bg-blue-50",    dot: "bg-blue-500",   text: "text-blue-700",   ring: "ring-blue-200",   label: "Information" },
  green:  { border: "border-l-emerald-500",bg: "bg-emerald-50", dot: "bg-emerald-500",text: "text-emerald-700",ring: "ring-emerald-200",label: "Completed" },
  gray:   { border: "border-l-slate-400",  bg: "bg-slate-50",   dot: "bg-slate-400",  text: "text-slate-600",  ring: "ring-slate-200",  label: "Waiting" },
  violet: { border: "border-l-violet-500", bg: "bg-violet-50",  dot: "bg-violet-500", text: "text-violet-700", ring: "ring-violet-200", label: "STR Active" },
};

// ── Card type definitions ──────────────────────────────────────────────────
const CARD_TYPES = {
  unanswered_message:  { label: "Unanswered Message",  icon: MessageSquare,        status: "orange", nextAction: "Reply to client",                          link: "/communications",               actionLabel: "Reply",  moduleKey: "messages"     },
  appointment_request: { label: "Appointment Request", icon: CalendarClock,        status: "orange", nextAction: "Confirm or decline appointment",            link: "/scheduling/appointments",      actionLabel: "Review", moduleKey: "appointments" },
  webchat_abandoned:   { label: "Abandoned Web Chat",  icon: MessageSquareWarning, status: "red",    nextAction: "Follow up — client may need immediate help", link: "/communications",               actionLabel: "Follow Up", moduleKey: "webchat"  },
  zoom_review:         { label: "Zoom Meeting Ready",  icon: Video,                status: "blue",   nextAction: "Review meeting & assign for analysis",       link: "/overview",                     actionLabel: "Review", moduleKey: "zoom"         },
  follow_up_due:       { label: "Follow-Up Due",       icon: CalendarClock,        status: "orange", nextAction: "Reconnect with Lead",                        link: null,                            actionLabel: "Open Record", moduleKey: "zoom"    },
  pending_payment_report: { label: "Payment Report",     icon: CreditCard,           status: "amber", nextAction: "Verify payment — post against invoice",             link: "/finance/payment-reports",      actionLabel: "Review",         moduleKey: "payment_report" },
  pending_activation:  { label: "Pending Activation",   icon: Zap,                  status: "orange", nextAction: "Activate engagement via Client Success",     link: null,                            actionLabel: "Activate",       moduleKey: "activation" },
  production_ready:    { label: "Production Ready",    icon: Briefcase,            status: "orange", nextAction: "Begin production",                           link: "/",                             actionLabel: "Open Workspace", moduleKey: "production" },
  str_active:          { label: "Strategic Review",    icon: BookOpen,             status: "violet", nextAction: "Continue Strategic Review",                  link: null,                            actionLabel: "Open Workspace", moduleKey: "str_active" },
  fbar_ready:          { label: "FBAR Ready to Prepare", icon: Globe,              status: "blue",   nextAction: "Prepare FBAR — client information complete",  link: null,                            actionLabel: "Open Production", moduleKey: "fbar"       },
  lifecycle_item:      { label: "Action Required · Staff", icon: Briefcase,        status: "orange", nextAction: "Open Workspace",                             link: null,                            actionLabel: "Open Workspace", moduleKey: "lifecycle"  },
  outstanding_invoice: { label: "ACTION REQUIRED · CLIENT", icon: CreditCard,      status: "orange", nextAction: "Waiting for client payment",                  link: null,                            actionLabel: "Open Workspace", moduleKey: "outstanding_invoices" },
  assigned_task:       { label: "Assigned Task",           icon: ClipboardList,    status: "orange", nextAction: "Complete assigned task",                     link: null,                            actionLabel: "Open Workspace", moduleKey: "tasks"      },
  portal_activated:    { label: "PORTAL ACCESS ACTIVATED", icon: ShieldCheck,      status: "green",  nextAction: "Continue onboarding",                        link: null,                            actionLabel: "Open Record",    moduleKey: "portal_activations" },
};

const MODULE_OPTIONS = [
  { key: "all",          label: "All",              type: null },
  { key: "messages",     label: "Messages",         type: "unanswered_message"  },
  { key: "appointments", label: "Appointments",     type: "appointment_request" },
  { key: "webchat",      label: "Webchat",          type: "webchat_abandoned"   },
  { key: "zoom",         label: "Zoom",             type: "zoom_review"             },
  { key: "payment_report", label: "Payment Reports",type: "pending_payment_report"  },
  { key: "outstanding_invoices", label: "Outstanding Invoices", type: "outstanding_invoice" },
  { key: "activation",  label: "Activation",       type: "pending_activation"  },
  { key: "production",  label: "Ready for Production",type: "production_ready"  },
  { key: "fbar",        label: "FBAR Ready",        type: "fbar_ready"          },
  { key: "str_active",  label: "Strategic Review",  type: "str_active"          },
  { key: "lifecycle",   label: "Lifecycle",         type: "lifecycle_item"      },
  { key: "tasks",      label: "Tasks",              type: "assigned_task"        },
  { key: "portal_activations", label: "Portal Access", type: "portal_activated" },
];

// ── Primary / secondary work-type split for the always-visible row ────────
// Ordered by operational frequency.  Secondary types live in the More ▼ dropdown.
const PRIMARY_MODULE_KEYS = new Set([
  "all", "lifecycle", "messages", "webchat", "outstanding_invoices", "portal_activations", "zoom",
]);

// ── Ordered display sections ───────────────────────────────────────────────
// Specialist categories win over generic responsibility groups.
// Every card maps to exactly one section key.
const SECTIONS = [
  { key: "new_leads",             label: "New Leads",                icon: Users,          testid: "section-new-leads"             },
  { key: "unanswered_messages",   label: "Unanswered Messages",      icon: MessageSquare,  testid: "section-unanswered-messages"   },
  { key: "ready_for_production",  label: "Ready for Production",     icon: Briefcase,      testid: "section-ready-for-production"  },
  { key: "action_required_staff", label: "Action Required · Staff",  icon: Zap,            testid: "section-action-required-staff" },
  { key: "action_required_client",label: "Action Required · Client", icon: Clock,          testid: "section-action-required-client"},
  { key: "outstanding_invoices",  label: "Outstanding Invoices",     icon: CreditCard,     testid: "section-outstanding-invoices"  },
  { key: "assigned_tasks",        label: "Assigned Tasks",           icon: ClipboardList,  testid: "section-assigned-tasks"        },
  { key: "zoom_review",           label: "Zoom Meeting Review",      icon: Video,          testid: "section-zoom-review"           },
  // Remaining lower-volume specialists — hidden when empty
  { key: "follow_up_client",      label: "Follow-Up Due",            icon: CalendarClock,  testid: "section-follow-up-due"         },
  { key: "payment_reports",       label: "Payment Reports",          icon: CreditCard,     testid: "section-payment-reports"       },
  { key: "strategic_review",      label: "Strategic Review Active",  icon: BookOpen,       testid: "section-strategic-review"      },
  { key: "third_party_pending",   label: "Third Party Pending",      icon: Clock,          testid: "section-third-party-pending"   },
  { key: "other",                 label: "Other",                    icon: Activity,       testid: "section-other"                 },
];

function getSectionKey(card) {
  // 1. NEW LEADS — follow_up_due cards for leads (not existing clients)
  if (card.type === "follow_up_due" && card.raw?.lifecycle_status === "lead") return "new_leads";
  // 2. UNANSWERED MESSAGES
  if (["unanswered_message", "appointment_request", "webchat_abandoned"].includes(card.type)) return "unanswered_messages";
  // 3. READY FOR PRODUCTION (specialist — wins over generic staff bucket)
  if (["production_ready", "pending_activation"].includes(card.type)) return "ready_for_production";
  // 4. ACTION REQUIRED · STAFF
  if (card.type === "lifecycle_item" && card.raw?.responsibility === "STAFF") return "action_required_staff";
  // portal_activated always lands in ACTION REQUIRED · STAFF
  if (card.type === "portal_activated") return "action_required_staff";
  // 5. ACTION REQUIRED · CLIENT (client has a pending obligation: sign, upload, complete organizer)
  if (card.type === "lifecycle_item" && card.raw?.responsibility === "WAITING_ON_CLIENT") return "action_required_client";
  // 6. OUTSTANDING INVOICES (specialist CLIENT section — awaiting payment)
  if (card.type === "outstanding_invoice") return "outstanding_invoices";
  // 7. ASSIGNED TASKS
  if (card.type === "assigned_task") return "assigned_tasks";
  // 8. ZOOM MEETING REVIEW (specialist — wins over staff action bucket)
  if (card.type === "zoom_review") return "zoom_review";
  // Remaining lower-volume specialists
  if (card.type === "follow_up_due") return "follow_up_client";          // non-lead client follow-ups
  if (card.type === "pending_payment_report") return "payment_reports";
  if (card.type === "str_active") return "strategic_review";
  if (card.type === "lifecycle_item") return "third_party_pending";      // WAITING_ON_THIRD_PARTY or unknown
  return "other";
}

// ── Responsibility resolution ──────────────────────────────────────────────
// ALL specialist producers now expose a canonical `responsibility` field on every
// item (added Oct 2026).  This function reads that field as the authoritative
// source; it NEVER infers responsibility from card type.
// Fallback logic is retained only as a safety net for any legacy items that
// pre-date the backend projection.
function getCardResponsibility(card) {
  const r = card.raw?.responsibility;
  // Canonical values from backend — responsibility is never inferred from card type.
  if (r === "WAITING_ON_CLIENT")       return "CLIENT";
  if (r === "WAITING_ON_THIRD_PARTY")  return "THIRD_PARTY";
  if (r === "STAFF")                   return "STAFF";
  // Backend always emits one of the three canonical values; this path should not be reached.
  return "STAFF";
}

function isCardUrgent(card) {
  if (card.type === "webchat_abandoned") return true;
  if (card.raw?.is_urgent) return true;
  return false;
}

function isCardOverdue(card) {
  if (card.raw?.is_overdue) return true;
  if (card.type === "follow_up_due" && card.followUpAt && new Date(card.followUpAt) < new Date()) return true;
  return false;
}

const MGMT_ROLES = new Set(["administrator", "partner", "manager"]);

// ── Helpers ────────────────────────────────────────────────────────────────
function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60)    return "just now";
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function extractClientName(item) {
  if (!item?.title) return "Unknown Client";
  const t = item.title;
  const dash  = t.indexOf(" — ");
  if (dash  > 0) return t.slice(0, dash).trim();
  const paren = t.indexOf(" (");
  if (paren > 0) return t.slice(0, paren).trim();
  return t.length > 40 ? t.slice(0, 40) + "…" : t;
}

function mapItem(type, item) {
  // For zoom cards the API provides explicit client_name and meeting title separately.
  // For production_ready, pending_activation, pending_payment_report, follow_up_due, str_active,
  // lifecycle_item, and portal_activated cards the API also provides client_name directly.
  // For other types the client name is parsed from the title string.
  const clientName = (type === "zoom_review" || type === "production_ready" || type === "pending_activation" || type === "pending_payment_report" || type === "follow_up_due" || type === "str_active" || type === "lifecycle_item" || type === "assigned_task" || type === "outstanding_invoice" || type === "portal_activated")
    ? (item.client_name || item.client_name || "Unknown")
    : extractClientName(item);

  // lifecycle_item uses a composite key matching the backend dedup key for stable identity:
  // (client_id, scope_type, scope_id|client_id, obligation_code)
  const rawId = type === "pending_activation"       ? item.engagement_id
              : type === "pending_payment_report"   ? item.report_id
              : type === "str_active"               ? item.id
              : type === "outstanding_invoice"       ? item.invoice_id
              : type === "lifecycle_item"           ? `${item.client_id}-${item.scope_type || 'client'}-${item.scope_id || item.client_id}-${item.obligation_code}`
              : type === "portal_activated"         ? item.client_id
              : item.id;

  // follow_up_due cards: nextAction depends on whether it's a lead or client
  const nextAction = type === "follow_up_due"
    ? (item.reconnect_label || (item.lifecycle_status === "lead" ? "Reconnect with Lead" : "Reconnect with Client"))
    : type === "lifecycle_item"
    ? item.next_action_label
    : undefined;

  return {
    id:           `${type[0]}-${rawId}`,
    rawId:        rawId,
    type,
    clientName:   type === "portal_activated" ? (item.client_name || "Unknown") : clientName,
    title:        item.title,
    subtitle:     item.subtitle,
    // Zoom-specific enrichment — undefined for other card types
    meetingTitle:   type === "zoom_review" ? (item.title || null) : null,
    intakeReason:   type === "zoom_review" ? (item.intake_reason || null) : null,
    // CRM identity fields — separate from staff assignment
    clientId:       (type === "zoom_review" || type === "follow_up_due" || type === "lifecycle_item" || type === "assigned_task" || type === "outstanding_invoice" || type === "portal_activated") ? (item.client_id || null) : null,
    clientNumber:   (type === "zoom_review" || type === "follow_up_due" || type === "lifecycle_item" || type === "assigned_task" || type === "outstanding_invoice") ? (item.client_number || null) : null,
    prospectName:   type === "zoom_review" ? (item.prospect_name || null) : null,
    topMatch:       type === "zoom_review" ? (item.top_match     || null) : null,
    // follow_up_due enrichment
    followUpAt:     type === "follow_up_due" ? (item.follow_up_at   || null) : null,
    followUpReason: type === "follow_up_due" ? (item.reason_label   || null) : null,
    followUpNote:   type === "follow_up_due" ? (item.note           || null) : null,
    hasSentCs:      type === "follow_up_due" ? (item.has_sent_cs    || false) : false,
    deferredAt:     type === "follow_up_due" ? (item.deferred_at    || null) : null,
    transcriptId:   type === "follow_up_due" ? (item.transcript_id  || null) : null,
    nextActionLabel: nextAction,
    // portal_activated enrichment
    lifecycleType:  type === "portal_activated" ? (item.lifecycle_type || null) : null,
    teamLabel:      type === "portal_activated" ? (item.team_label     || null) : null,
    activatedAt:    type === "portal_activated" ? (item.activated_at   || null) : null,
    link:         type === "follow_up_due"
                    ? (item.link || (item.client_id ? `/client-workspace/${item.client_id}` : null))
                    : type === "str_active"
                    ? (item.str_workspace_target || (item.client_id ? `/client-success/strategic-review/${item.client_id}` : null))
                    : type === "lifecycle_item"
                    ? (item.destination || (item.client_id ? `/client-workspace/${item.client_id}` : null))
                    : type === "portal_activated"
                    ? item.link
                    : (item.link || (type === "production_ready" ? item.destination : null) || CARD_TYPES[type]?.link),
    // pending_activation items carry signed_at; pending_payment_report carry created_at (submitted);
    // all others use the standard created/updated timestamps
    timeAgo:      timeAgo(
      type === "pending_activation"     ? item.signed_at
      : type === "follow_up_due"        ? item.follow_up_at
      : type === "outstanding_invoice"  ? item.issue_date
      : type === "portal_activated"     ? item.activated_at
      : item.created_at || item.updated_at || item.detected_at
    ),
    assigneeId:   item.assigned_to_id   || null,
    assigneeName: item.assigned_to_name || null,
    raw:          item,
  };
}

const TYPE_MAP = {
  unanswered_message:    "message",
  appointment_request:   "appointment",
  webchat_abandoned:     "webchat",
  zoom_review:           "zoom",
  pending_payment_report: "payment_report",
  pending_activation:    "engagement",
  production_ready:      "production",
  str_active:            "str",
  outstanding_invoice:   "invoice",
};

// ── Sub-components ─────────────────────────────────────────────────────────

function MetricChip({ label, value, color, icon: Icon, testid }) {
  return (
    <div data-testid={testid}
      className="flex-1 min-w-[130px] bg-white border border-slate-200 rounded-xl px-4 py-3.5 flex flex-col gap-1 cursor-default hover:-translate-y-0.5 transition-transform duration-150">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400">{label}</span>
        {Icon && <Icon className={cn("h-3.5 w-3.5", color || "text-slate-400")} />}
      </div>
      <span className={cn("text-2xl font-black tracking-tight", color || "text-slate-900")}>
        {value ?? "—"}
      </span>
    </div>
  );
}

function PillButton({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors whitespace-nowrap",
        active
          ? "bg-[#2F80ED] text-white shadow-sm"
          : "bg-white text-slate-600 border border-slate-200 hover:border-slate-300 hover:text-slate-900"
      )}
    >
      {children}
    </button>
  );
}

function AssigneeDropdown({ staffList, value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const current = value === "all" ? null : value === "unassigned" ? { id: "unassigned", name: "Unassigned" }
    : staffList.find((s) => s.id === value);
  const label = current ? current.name : "All Staff";

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="assignee-filter-btn"
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium border transition-colors",
          value && value !== "all"
            ? "bg-[#2F80ED] text-white border-[#2F80ED] shadow-sm"
            : "bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:text-slate-900"
        )}
      >
        <User className="h-3.5 w-3.5" />
        {label}
        <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-180")} />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1.5 z-50 w-56 bg-white border border-slate-200 rounded-xl shadow-xl py-1.5 animate-in fade-in slide-in-from-top-1 duration-150"
          data-testid="assignee-dropdown">
          <button
            onClick={() => { onChange("all"); setOpen(false); }}
            className={cn("w-full text-left px-3.5 py-2 text-sm hover:bg-slate-50", !value || value === "all" ? "font-semibold text-[#2F80ED]" : "text-slate-700")}
          >All Staff</button>
          <button
            onClick={() => { onChange("unassigned"); setOpen(false); }}
            data-testid="assignee-opt-unassigned"
            className={cn("w-full text-left px-3.5 py-2 text-sm hover:bg-slate-50 border-t border-slate-100", value === "unassigned" ? "font-semibold text-amber-600" : "text-slate-700")}
          >Unassigned</button>
          {staffList.length > 0 && <div className="h-px bg-slate-100 my-1" />}
          {staffList.map((s) => (
            <button
              key={s.id}
              onClick={() => { onChange(s.id); setOpen(false); }}
              data-testid={`assignee-opt-${s.id}`}
              className={cn("w-full text-left px-3.5 py-2 text-sm hover:bg-slate-50", value === s.id ? "font-semibold text-[#2F80ED]" : "text-slate-700")}
            >
              <span className="block truncate">{s.name}</span>
              {s.title && <span className="block text-[11px] text-slate-400 truncate">{s.title}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function AssignButton({ card, staffList, onAssigned, label = "Assign" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const assign = async (staff) => {
    setBusy(true);
    setOpen(false);
    try {
      await api.post("/notifications/assign-card", {
        item_id:     card.rawId,
        item_type:   TYPE_MAP[card.type] || "message",
        assignee_id: staff.id,
      });
      toast.success(`Assigned to ${staff.name}`);
      onAssigned?.(card.id, staff.id, staff.name);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Assignment failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        disabled={busy}
        data-testid={`assign-btn-${card.rawId}`}
        className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600 hover:text-white hover:bg-amber-500 border border-amber-400/60 hover:border-amber-500 rounded-lg px-2.5 py-1.5 transition-colors disabled:opacity-50"
        title="Assign to staff"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <User className="h-3.5 w-3.5" />}
        {label}
      </button>

      {open && (
        <div
          data-testid={`assign-dropdown-${card.rawId}`}
          className="absolute bottom-full mb-1.5 right-0 z-50 w-52 bg-white border border-slate-200 rounded-xl shadow-xl py-1.5 animate-in fade-in slide-in-from-bottom-1 duration-150"
        >
          <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400 border-b border-slate-100">
            Assign to
          </div>
          <div className="max-h-52 overflow-y-auto" style={{ scrollbarWidth: "thin" }}>
            {staffList.map((s) => (
              <button
                key={s.id}
                onClick={() => assign(s)}
                data-testid={`assign-staff-${s.id}`}
                className="w-full text-left px-3.5 py-2 hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-2.5">
                  <div className="h-6 w-6 rounded-full bg-[#1C2431] text-white flex items-center justify-center text-[10px] font-bold shrink-0">
                    {(s.name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">{s.name}</div>
                    {s.title && <div className="text-[10px] text-slate-400 truncate">{s.title}</div>}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Compact risk pill (Urgent / Overdue) ───────────────────────────────────
function CompactRiskPill({ label, count, active, color, onClick, testid }) {
  const colors = {
    red: {
      active:   "bg-red-500 text-white border-red-500",
      inactive: "bg-white text-red-600 border-red-200 hover:border-red-400 hover:bg-red-50",
      badge:    "bg-white/25 text-white",
      badgeOff: "bg-red-100 text-red-700",
    },
    rose: {
      active:   "bg-rose-600 text-white border-rose-600",
      inactive: "bg-white text-rose-600 border-rose-200 hover:border-rose-400 hover:bg-rose-50",
      badge:    "bg-white/25 text-white",
      badgeOff: "bg-rose-100 text-rose-700",
    },
  };
  const cfg = colors[color] || colors.red;
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors whitespace-nowrap",
        active ? cfg.active : cfg.inactive
      )}
    >
      <AlertTriangle className="h-3 w-3 shrink-0" />
      {label}
      <span className={cn("rounded-full px-1.5 py-0.5 text-[10px] font-bold", active ? cfg.badge : cfg.badgeOff)}>
        {count ?? 0}
      </span>
    </button>
  );
}

// ── Consolidated ownership selector ────────────────────────────────────────
function OwnershipSelector({ viewMode, assigneeFilter, staffList, onChangeMode, onChangeAssignee }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const currentLabel =
    viewMode === "my_work"      ? "Me"
    : assigneeFilter === "unassigned" ? "Unassigned"
    : assigneeFilter !== "all"
      ? (staffList.find((s) => s.id === assigneeFilter)?.name || "Staff member")
      : "All Staff";

  const isFiltered = viewMode === "my_work" || assigneeFilter !== "all";

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="ownership-selector-btn"
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium border transition-colors whitespace-nowrap",
          isFiltered
            ? "bg-[#2F80ED] text-white border-[#2F80ED] shadow-sm"
            : "bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:text-slate-900"
        )}
      >
        <User className="h-3.5 w-3.5 shrink-0" />
        <span>Assigned to: <span className="font-bold">{currentLabel}</span></span>
        <ChevronDown className={cn("h-3.5 w-3.5 transition-transform shrink-0", open && "rotate-180")} />
      </button>

      {open && (
        <div
          data-testid="ownership-dropdown"
          className="absolute top-full left-0 mt-1.5 z-50 w-60 bg-white border border-slate-200 rounded-xl shadow-xl py-1.5 animate-in fade-in slide-in-from-top-1 duration-150"
        >
          <button
            onClick={() => { onChangeMode("all"); onChangeAssignee("all"); setOpen(false); }}
            data-testid="ownership-opt-all-staff"
            className={cn("w-full text-left px-3.5 py-2 text-sm hover:bg-slate-50",
              viewMode === "all" && assigneeFilter === "all" ? "font-semibold text-[#2F80ED]" : "text-slate-700")}
          >All Staff</button>
          <button
            onClick={() => { onChangeMode("my_work"); onChangeAssignee("all"); setOpen(false); }}
            data-testid="ownership-opt-me"
            className={cn("w-full text-left px-3.5 py-2 text-sm hover:bg-slate-50 border-t border-slate-100",
              viewMode === "my_work" ? "font-semibold text-[#2F80ED]" : "text-slate-700")}
          >Me</button>
          <button
            onClick={() => { onChangeMode("all"); onChangeAssignee("unassigned"); setOpen(false); }}
            data-testid="ownership-opt-unassigned"
            className={cn("w-full text-left px-3.5 py-2 text-sm hover:bg-slate-50",
              assigneeFilter === "unassigned" ? "font-semibold text-amber-600" : "text-slate-700")}
          >Unassigned</button>
          {staffList.length > 0 && <div className="h-px bg-slate-100 my-1" />}
          {staffList.map((s) => (
            <button
              key={s.id}
              onClick={() => { onChangeMode("all"); onChangeAssignee(s.id); setOpen(false); }}
              data-testid={`ownership-opt-${s.id}`}
              className={cn("w-full text-left px-3.5 py-2 text-sm hover:bg-slate-50",
                assigneeFilter === s.id ? "font-semibold text-[#2F80ED]" : "text-slate-700")}
            >
              <span className="block truncate">{s.name}</span>
              {s.title && <span className="block text-[11px] text-slate-400 truncate">{s.title}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CardAssignmentFooter({ card, staffList, isManagement, onAssigned }) {
  const navigate = useNavigate();
  const isAssigned = !!card.assigneeName;
  const staffInfo  = staffList.find((s) => s.id === card.assigneeId);
  const subtitle   = staffInfo?.title || null;
  const initials   = isAssigned
    ? (card.assigneeName || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()
    : null;

  // ── Non-zoom cards: unchanged ─────────────────────────────────────────────
  if (card.type !== "zoom_review") {
    return (
      <div data-testid={`live-ops-assignment-footer-${card.rawId}`}>
        <p className="text-[10px] font-semibold uppercase tracking-[0.07em] text-slate-400 mb-1.5">
          Assigned To
        </p>
        {isAssigned ? (
          <div className="flex items-center gap-2.5">
            <div className="h-7 w-7 rounded-full bg-[#1C2431] text-white flex items-center justify-center text-[10px] font-bold shrink-0 ring-2 ring-emerald-300/50">
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-slate-800 truncate"
                data-testid={`live-ops-card-assignee-${card.rawId}`}>
                {card.assigneeName}
              </div>
              {subtitle && <div className="text-[11px] text-slate-400 truncate">{subtitle}</div>}
            </div>
            <div className="h-2 w-2 rounded-full bg-emerald-400 shrink-0" title="Assigned" />
          </div>
        ) : (
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-start gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />
              <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.06em] text-amber-600">Unassigned</div>
                <div className="text-[11px] text-slate-400">Needs Assignment</div>
              </div>
            </div>
            {isManagement && (
              <AssignButton card={card} staffList={staffList} onAssigned={onAssigned} />
            )}
          </div>
        )}
      </div>
    );
  }

  // ── Zoom cards: two separate dimensions ──────────────────────────────────
  const isMatched = !!card.clientId;

  // Unmatched zoom meeting ─ primary unresolved step is CRM association
  if (!isMatched) {
    const possibleName = card.prospectName || card.topMatch?.name || null;
    const possibleType = card.topMatch?.type || null;

    return (
      <div data-testid={`live-ops-assignment-footer-${card.rawId}`}>
        {/* Possible identity from matching pipeline — clearly advisory */}
        {possibleName && (
          <div className="mb-2.5">
            <p className="text-[10px] font-semibold uppercase tracking-[0.07em] text-slate-400 mb-1">
              Possible Client / Lead
            </p>
            <div className="flex items-center gap-1.5 flex-wrap">
              <User className="h-3.5 w-3.5 text-slate-400 shrink-0" />
              <span className="text-sm font-medium text-slate-700" data-testid={`live-ops-possible-name-${card.rawId}`}>
                {possibleName}
              </span>
              {possibleType && (
                <span className="text-[11px] text-slate-400 bg-slate-100 rounded px-1.5 py-0.5">
                  {possibleType}
                </span>
              )}
            </div>
            <p className="text-[11px] text-slate-400 mt-0.5 ml-5">Detected from meeting context</p>
          </div>
        )}

        {/* CRM Association: Review Required */}
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.07em] text-slate-400 mb-1">
            CRM Association
          </p>
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />
              <span className="text-[11px] font-bold uppercase tracking-[0.06em] text-amber-600"
                data-testid={`live-ops-crm-not-matched-${card.rawId}`}>
                Review Required
              </span>
            </div>
            <button
              onClick={() => navigate(`/meeting-review/${card.rawId}`)}
              data-testid={`live-ops-match-client-btn-${card.rawId}`}
              className="inline-flex items-center gap-1 text-xs font-semibold text-[#2F80ED] hover:text-white hover:bg-[#2F80ED] border border-[#2F80ED]/40 hover:border-[#2F80ED] rounded-lg px-2.5 py-1.5 transition-colors shrink-0"
            >
              <User className="h-3.5 w-3.5" /> Review &amp; Match
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Matched zoom meeting ─ show confirmed client only; no staff assignment
  return (
    <div data-testid={`live-ops-assignment-footer-${card.rawId}`}>
      {/* Confirmed Client / Lead */}
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-[0.07em] text-slate-400 mb-1">
          Client / Lead
        </p>
        <div className="flex items-center gap-1.5 flex-wrap">
          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
          <span className="text-sm font-semibold text-slate-800"
            data-testid={`live-ops-crm-client-${card.rawId}`}>
            {card.clientName}
          </span>
          {card.clientNumber && (
            <span className="text-[11px] text-slate-400">{card.clientNumber}</span>
          )}
          <span className="text-[11px] font-semibold text-emerald-600 bg-emerald-50 border border-emerald-100 rounded px-1.5 py-0.5">
            Matched
          </span>
        </div>
      </div>
    </div>
  );
}

function PendingPaymentReportFooter({ card }) {
  const ccyFmt = (v, ccy = "USD") => {
    try { return new Intl.NumberFormat("en-US", { style: "currency", currency: ccy }).format(Number(v || 0)); }
    catch { return `${ccy} ${Number(v || 0).toFixed(2)}`; }
  };
  return (
    <div data-testid={`live-ops-assignment-footer-${card.rawId}`}>
      <p className="text-[10px] font-semibold uppercase tracking-[0.07em] text-slate-400 mb-1">Client</p>
      <div className="flex items-center gap-1.5 flex-wrap">
        <CreditCard className="h-3.5 w-3.5 text-amber-500 shrink-0" />
        <span className="text-sm font-semibold text-slate-800"
          data-testid={`live-ops-payment-report-client-${card.rawId}`}>
          {card.clientName}
        </span>
        {card.raw.client_number && (
          <span className="text-[11px] text-slate-400">{card.raw.client_number}</span>
        )}
      </div>
      {card.raw.invoice_number && (
        <p className="text-[11px] text-slate-400 mt-0.5">{card.raw.invoice_number}</p>
      )}
      {card.raw.currency && (
        <p className="text-[11px] font-semibold text-amber-700 mt-0.5">
          {ccyFmt(card.raw.invoice_balance || card.raw.amount_reported, card.raw.currency)}
          {card.raw.fee_amount > 0 && (
            <span className="text-slate-400 font-normal"> + fee</span>
          )}
        </p>
      )}
    </div>
  );
}

function PendingActivationFooter({ card }) {
  return (
    <div data-testid={`live-ops-assignment-footer-${card.rawId}`}>
      <p className="text-[10px] font-semibold uppercase tracking-[0.07em] text-slate-400 mb-1">
        Client
      </p>
      <div className="flex items-center gap-1.5 flex-wrap">
        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
        <span className="text-sm font-semibold text-slate-800"
          data-testid={`live-ops-activation-client-${card.rawId}`}>
          {card.clientName}
        </span>
        {card.raw.client_number && (
          <span className="text-[11px] text-slate-400">{card.raw.client_number}</span>
        )}
      </div>
      {card.raw.engagement_number && (
        <p className="text-[11px] text-slate-400 mt-0.5">{card.raw.engagement_number}</p>
      )}
      {card.raw.is_str && (
        <p className="text-[11px] text-violet-600 font-medium mt-0.5">Strategic Tax Review</p>
      )}
    </div>
  );
}

function ProductionReadyFooter({ card }) {
  return (
    <div data-testid={`live-ops-assignment-footer-${card.rawId}`}>
      <p className="text-[10px] font-semibold uppercase tracking-[0.07em] text-slate-400 mb-1">
        Client
      </p>
      <div className="flex items-center gap-1.5 flex-wrap">
        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
        <span className="text-sm font-semibold text-slate-800"
          data-testid={`live-ops-production-client-${card.rawId}`}>
          {card.clientName}
        </span>
        {card.raw.client_number && (
          <span className="text-[11px] text-slate-400">{card.raw.client_number}</span>
        )}
      </div>
      {card.raw.engagement_number && (
        <p className="text-[11px] text-slate-400 mt-0.5">{card.raw.engagement_number}</p>
      )}
      {card.raw.gate_cleared_at && (
        <p className="text-[11px] text-slate-400 mt-0.5">
          Authorized {timeAgo(card.raw.gate_cleared_at)}
        </p>
      )}
    </div>
  );
}

function StrActiveFooter({ card }) {
  const assignedIndividual = card.raw.assigned_to_name   || null;
  const assignedTeamName   = card.raw.assigned_team_name || null;
  const engNumber          = card.raw.engagement_number  || null;

  return (
    <div data-testid={`live-ops-str-footer-${card.rawId}`}>
      <p className="text-[10px] font-semibold uppercase tracking-[0.07em] text-slate-400 mb-1">
        Client
      </p>
      <div className="flex items-center gap-1.5 flex-wrap">
        <BookOpen className="h-3.5 w-3.5 text-violet-500 shrink-0" />
        <span className="text-sm font-semibold text-slate-800"
          data-testid={`live-ops-str-client-${card.rawId}`}>
          {card.clientName}
        </span>
        {card.raw.client_number && (
          <span className="text-[11px] text-slate-400">{card.raw.client_number}</span>
        )}
      </div>
      {engNumber && (
        <p className="text-[11px] text-slate-400 mt-0.5" data-testid={`live-ops-str-eng-${card.rawId}`}>
          {engNumber}
        </p>
      )}
      {/* Canonical ownership — Assigned Individual + Assigned Team only */}
      <div className="mt-1.5 space-y-0.5">
        {assignedIndividual ? (
          <p className="text-[11px] text-slate-500" data-testid={`live-ops-str-assigned-individual-${card.rawId}`}>
            <span className="font-medium text-slate-600">Assigned Individual:</span> {assignedIndividual}
          </p>
        ) : (
          <p className="text-[11px] text-amber-600 font-medium" data-testid={`live-ops-str-assigned-individual-${card.rawId}`}>
            Assigned Individual: Unassigned
          </p>
        )}
        {assignedTeamName ? (
          <p className="text-[11px] text-slate-500" data-testid={`live-ops-str-assigned-team-${card.rawId}`}>
            <span className="font-medium text-slate-600">Assigned Team:</span> {assignedTeamName}
          </p>
        ) : (
          <p className="text-[11px] text-slate-400" data-testid={`live-ops-str-assigned-team-${card.rawId}`}>
            <span className="font-medium text-slate-500">Assigned Team:</span> Unassigned
          </p>
        )}
      </div>
    </div>
  );
}

// Responsibility display configuration for lifecycle_item cards
const LIFECYCLE_RESP_CONFIG = {
  STAFF:                { icon: Zap,   label: "Action Required · Staff",   cls: "text-amber-700 bg-amber-50 border-amber-200"  },
  WAITING_ON_CLIENT:    { icon: Clock, label: "Waiting on Client",         cls: "text-slate-600 bg-slate-50 border-slate-200"  },
  WAITING_ON_THIRD_PARTY: { icon: Clock, label: "Waiting on Third Party",  cls: "text-blue-600 bg-blue-50 border-blue-200"     },
};

const LIFECYCLE_RESP_STATUS = {
  STAFF:                "orange",
  WAITING_ON_CLIENT:    "gray",
  WAITING_ON_THIRD_PARTY: "blue",
};

function LiveOpsLifecycleFooter({ card }) {
  const navigate   = useNavigate();
  const resp       = card.raw?.responsibility || "STAFF";
  const cfg        = LIFECYCLE_RESP_CONFIG[resp] || LIFECYCLE_RESP_CONFIG.STAFF;
  const RespIcon   = cfg.icon;

  return (
    <div data-testid={`live-ops-lifecycle-footer-${card.rawId}`}>
      <p className="text-[10px] font-semibold uppercase tracking-[0.07em] text-slate-400 mb-1">
        Client
      </p>
      <div className="flex items-center gap-1.5 flex-wrap mb-2">
        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
        <button
          onClick={() => navigate(card.raw?.destination || `/client-workspace/${card.raw?.client_id}`)}
          className="text-sm font-semibold text-slate-800 hover:text-[#2F80ED] transition-colors"
          data-testid={`live-ops-lifecycle-client-${card.rawId}`}
        >
          {card.clientName}
        </button>
        {card.raw?.client_number && (
          <span className="text-[11px] text-slate-400">{card.raw.client_number}</span>
        )}
      </div>
      <div
        className={cn("inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[11px] font-semibold border", cfg.cls)}
        data-testid={`live-ops-lifecycle-responsibility-${card.rawId}`}
      >
        <RespIcon className="h-3 w-3 shrink-0" />
        {cfg.label}
      </div>
    </div>
  );
}

function LiveOpsCard({ card, onRefresh, staffList, isManagement, onAssigned }) {
  const navigate = useNavigate();
  // ⋯ menu state (zoom_review and follow_up_due cards)
  const [showMenu, setShowMenu] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [archiveBusy, setArchiveBusy] = useState(false);
  // Follow Up Later dialog state
  const [showFollowUpDialog, setShowFollowUpDialog] = useState(false);
  const [fuDate,   setFuDate]   = useState("");
  const [fuReason, setFuReason] = useState("future_service_need");
  const [fuNote,   setFuNote]   = useState("");
  const [fuBusy,   setFuBusy]   = useState(false);
  // No Further Action (from zoom_review card)
  const [nfaBusy,  setNfaBusy]  = useState(false);
  // Activation dialog (pending_activation cards only)
  const [showActivateDialog, setShowActivateDialog] = useState(false);
  const [activateBusy, setActivateBusy] = useState(false);
  // Delete task dialog (assigned_task cards only)
  const [showDeleteTaskDialog, setShowDeleteTaskDialog] = useState(false);
  const [deleteTaskBusy, setDeleteTaskBusy] = useState(false);
  // Nudge Client dialog (outstanding_invoice cards only)
  const [showNudgeDialog, setShowNudgeDialog] = useState(false);
  const [nudgeBusy, setNudgeBusy] = useState(false);
  const menuRef = useRef(null);
  const def = CARD_TYPES[card.type] || CARD_TYPES.unanswered_message;
  // STR cards: derive status color from the canonical projected state key.
  // supplemental_notification_failed = staff owns the blocker → orange.
  // awaiting_supplemental_client = client owns the blocker → gray (waiting).
  // All other STR states keep the default violet treatment.
  const strStateOverride =
    card.type === "str_active" && card.raw?.str_state === "supplemental_notification_failed"
      ? "orange"
      : card.type === "str_active" && card.raw?.str_state === "awaiting_supplemental_client"
      ? "gray"
      : null;
  // lifecycle_item: responsibility drives the status color and header label
  const lifecycleStatusKey  = card.type === "lifecycle_item"
    ? (LIFECYCLE_RESP_STATUS[card.raw?.responsibility] || "orange")
    : null;
  const st  = STATUS[lifecycleStatusKey || strStateOverride || def.status] || STATUS.orange;
  // Badge label: for lifecycle_item, responsibility drives the label (not def.label)
  const cardBadgeLabel = card.type === "lifecycle_item"
    ? ((LIFECYCLE_RESP_CONFIG[card.raw?.responsibility] || LIFECYCLE_RESP_CONFIG.STAFF).label)
    : def.label;
  const Icon = def.icon;

  const REASON_OPTIONS = [
    { value: "future_service_need",    label: "Future Service Need" },
    { value: "waiting_for_tax_period", label: "Waiting for Tax/Filing Period" },
    { value: "waiting_for_client",     label: "Waiting for Client" },
    { value: "waiting_for_life_event", label: "Waiting for Life/Business Event" },
    { value: "planning_opportunity",   label: "Planning Opportunity" },
    { value: "other",                  label: "Other" },
  ];

  // Close menu on outside click
  useEffect(() => {
    if (!showMenu) return;
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showMenu]);

  const handleAction = async () => {
    if (card.type === "zoom_review" && card.rawId) {
      try { await api.post(`/meeting-transcripts/${card.rawId}/zoom-viewed`); } catch (_) {}
      navigate(`/meeting-review/${card.rawId}`);
      return;
    }
    if (card.type === "pending_activation") {
      setShowActivateDialog(true);
      return;
    }
    if (card.type === "pending_payment_report") {
      navigate(`/finance/payment-reports?open=${card.rawId}`);
      return;
    }
    if (card.type === "production_ready") {
      // For STR: use the canonical state-aware workspace target (may include ?tab=findings etc.)
      const target = card.raw.is_str
        ? (card.raw.str_workspace_target || card.raw.destination)
        : card.raw.destination;
      navigate(target || card.link);
      return;
    }
    if (card.type === "str_active") {
      navigate(card.link || `/client-success/strategic-review/${card.raw.client_id}`);
      return;
    }
    if (card.type === "lifecycle_item") {
      navigate(card.raw?.destination || `/client-workspace/${card.raw?.client_id}`);
      return;
    }
    navigate(card.link || def.link);
  };

  const handleFollowUpLater = async () => {
    if (!fuDate) { toast.error("Please select a follow-up date"); return; }
    setFuBusy(true);
    try {
      await api.post(`/meeting-followups/by-transcript/${card.rawId}/defer`, {
        follow_up_at: fuDate,
        reason: fuReason,
        note: fuNote,
      });
      toast.success("Follow-up deferred — card removed from Live Operations");
      setShowFollowUpDialog(false);
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to defer follow-up");
    } finally {
      setFuBusy(false);
    }
  };

  const handleNoFurtherActionZoom = async () => {
    // "No Further Action" on a zoom_review card creates a follow-up in closed state.
    // We do this by first deferring to today (creates the record) then immediately closing it.
    // Simpler: Use the follow-up endpoint to check if a record already exists, else create then close.
    // For now: create a deferred follow-up with today's date then close it.
    setNfaBusy(true);
    setShowMenu(false);
    try {
      const resp = await api.post(`/meeting-followups/by-transcript/${card.rawId}/defer`, {
        follow_up_at: new Date().toISOString().slice(0, 10),
        reason: "other",
        note: "No further action selected.",
      });
      if (resp.data?.id) {
        await api.post(`/meeting-followups/${resp.data.id}/close`);
      }
      toast.success("No further action — card removed from Live Operations");
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to close follow-up");
    } finally {
      setNfaBusy(false);
    }
  };

  return (
    <div
      data-testid={`live-ops-card-${card.rawId}`}
      className={cn(
        "bg-white border border-slate-200 rounded-xl border-l-4 flex flex-col",
        "hover:shadow-md hover:-translate-y-0.5 transition-all duration-200",
        "animate-in fade-in slide-in-from-bottom-2 duration-300",
        st.border
      )}
    >
      {/* Header */}
      <div className="px-4 pt-4 pb-3 flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <span className={cn("inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.07em] rounded-full px-2.5 py-0.5 ring-1 mb-1.5", st.text, st.bg, st.ring)}>
            <span className={cn("h-1.5 w-1.5 rounded-full", st.dot)} />
            {cardBadgeLabel}
          </span>
          <div className="text-base font-bold text-slate-900 leading-snug truncate" data-testid={`live-ops-card-client-${card.rawId}`}>
            {card.clientName}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {/* ⋯ exception menu — Zoom cards and follow_up_due cards */}
          {(card.type === "zoom_review" || card.type === "follow_up_due") && card.rawId && (
            <div className="relative" ref={menuRef}>
              <button
                onClick={(e) => { e.stopPropagation(); setShowMenu((v) => !v); }}
                className="h-7 w-7 rounded-md flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                data-testid={`live-ops-zoom-menu-${card.rawId}`}
                title="Meeting options"
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>
              {showMenu && (
                <div className="absolute right-0 top-full mt-1 z-50 w-60 bg-white rounded-xl shadow-lg border border-slate-200 py-1 animate-in fade-in slide-in-from-top-2 duration-150">
                  {card.type === "zoom_review" && <>
                    <button
                      onClick={() => { setShowMenu(false); setFuDate(""); setFuReason("future_service_need"); setFuNote(""); setShowFollowUpDialog(true); }}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                      data-testid={`live-ops-zoom-followup-btn-${card.rawId}`}
                    >
                      <Calendar className="h-4 w-4 shrink-0" />
                      Follow Up Later
                    </button>
                    <button
                      onClick={() => { setShowMenu(false); setShowArchiveDialog(true); }}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 hover:bg-amber-50 hover:text-amber-700 transition-colors"
                      data-testid={`live-ops-zoom-archive-btn-${card.rawId}`}
                    >
                      <Archive className="h-4 w-4 shrink-0" />
                      Archive / Not Client Related
                    </button>
                    <div className="my-1 border-t border-slate-100" />
                    <button
                      onClick={handleNoFurtherActionZoom}
                      disabled={nfaBusy}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-colors disabled:opacity-50"
                      data-testid={`live-ops-zoom-nfa-btn-${card.rawId}`}
                    >
                      {nfaBusy ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> : <X className="h-4 w-4 shrink-0" />}
                      No Further Action
                    </button>
                  </>}
                  {card.type === "follow_up_due" && <>
                    <button
                      onClick={() => { setShowMenu(false); if (card.clientId) navigate(`/client-workspace/${card.clientId}`); }}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                      data-testid={`live-ops-fu-open-btn-${card.rawId}`}
                    >
                      <ArrowRight className="h-4 w-4 shrink-0" />
                      Open Lead Record
                    </button>
                  </>}
                </div>
              )}
            </div>
          )}
          {/* ⋯ action menu — assigned_task cards */}
          {card.type === "assigned_task" && card.rawId && card.clientId && (
            <div className="relative" ref={menuRef}>
              <button
                onClick={(e) => { e.stopPropagation(); setShowMenu((v) => !v); }}
                className="h-7 w-7 rounded-md flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                data-testid={`live-ops-task-menu-${card.rawId}`}
                title="Task options"
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>
              {showMenu && (
                <div className="absolute right-0 top-full mt-1 z-50 w-52 bg-white rounded-xl shadow-lg border border-slate-200 py-1 animate-in fade-in slide-in-from-top-2 duration-150">
                  <button
                    onClick={() => { setShowMenu(false); setShowDeleteTaskDialog(true); }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    data-testid={`live-ops-task-delete-btn-${card.rawId}`}
                  >
                    <Trash2 className="h-4 w-4 shrink-0" />
                    Delete Task
                  </button>
                </div>
              )}
            </div>
          )}
          <Icon className={cn("h-5 w-5 shrink-0 mt-0.5", st.text)} />
        </div>
      </div>

      {/* Body */}
      <div className="px-4 pb-3 flex-1">
        {card.type === "follow_up_due" ? (
          <div className="space-y-1.5" data-testid={`live-ops-fu-body-${card.rawId}`}>
            <p className="text-sm text-slate-700 font-medium">Prior consultation completed</p>
            {card.hasSentCs && (
              <p className="text-xs text-slate-500 flex items-center gap-1.5">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                Consultation Summary sent
              </p>
            )}
            {card.deferredAt && (
              <p className="text-xs text-slate-400">Deferred {card.deferredAt.slice(0, 10)}</p>
            )}
            {card.followUpAt && (
              <p className="text-xs text-amber-600 font-semibold">
                Follow-up due {new Date(card.followUpAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
              </p>
            )}
            {card.followUpNote && (
              <p className="text-xs text-slate-500 italic line-clamp-2">{card.followUpNote}</p>
            )}
          </div>
        ) : card.type === "pending_payment_report" ? (
          <div className="space-y-1.5" data-testid={`live-ops-payment-report-body-${card.rawId}`}>
            {(() => {
              const ccyFmt = (v, ccy = "USD") => {
                try { return new Intl.NumberFormat("en-US", { style: "currency", currency: ccy }).format(Number(v || 0)); }
                catch { return `${ccy} ${Number(v || 0).toFixed(2)}`; }
              };
              const METHOD_LABEL = {
                stripe: "Stripe", wise: "Wise Transfer", paypal: "PayPal",
                thai_bank: "Thai Bank", thai_qr: "Thai QR", wire: "Wire Transfer",
                ach: "ACH", bank_transfer: "Bank Transfer", manual: "Manual",
              };
              const methodLabel = METHOD_LABEL[card.raw.method_code] || card.raw.method_code?.replace(/_/g, " ");
              return (
                <>
                  <p className="text-sm font-semibold text-slate-800">
                    {ccyFmt(card.raw.invoice_balance || card.raw.amount_reported, card.raw.currency)}
                  </p>
                  <p className="text-xs text-slate-600">{methodLabel}</p>
                  {card.raw.days_pending != null && (
                    <div className="flex items-center gap-1.5 text-xs text-amber-600 mt-1">
                      <Clock className="h-3 w-3 shrink-0" />
                      Submitted {card.raw.days_pending === 0 ? "today" : `${card.raw.days_pending}d ago`} — awaiting verification
                    </div>
                  )}
                  {card.raw.reference_number && (
                    <p className="text-[11px] text-slate-400 font-mono">{card.raw.reference_number}</p>
                  )}
                </>
              );
            })()}
          </div>
        ) : card.type === "outstanding_invoice" ? (
          <div className="space-y-1.5" data-testid={`live-ops-invoice-body-${card.rawId}`}>
            {(() => {
              const ccyFmt = (v, ccy = "USD") => {
                try { return new Intl.NumberFormat("en-US", { style: "currency", currency: ccy }).format(Number(v || 0)); }
                catch { return `${ccy} ${Number(v || 0).toFixed(2)}`; }
              };
              const fmtDate = (d) => {
                if (!d) return null;
                try { return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
                catch { return null; }
              };
              const inv = card.raw || {};
              const STATUS_LABEL = {
                issued:           "Issued",
                due:              "Due",
                initiated:        "Initiated",
                awaiting_payment: "Awaiting Payment",
                payment_hold:     "Payment Hold",
                partially_paid:   "Partially Paid",
                overpayment:      "Overpayment",
              };
              return (
                <>
                  <div className="flex items-baseline gap-2">
                    <p className="text-base font-bold text-slate-900" data-testid={`live-ops-invoice-balance-${card.rawId}`}>
                      {ccyFmt(inv.balance, inv.currency)}
                    </p>
                    <span className="text-[11px] text-slate-400">{inv.currency}</span>
                  </div>
                  {inv.invoice_number && (
                    <p className="text-xs text-slate-500 font-mono" data-testid={`live-ops-invoice-number-${card.rawId}`}>
                      {inv.invoice_number}
                    </p>
                  )}
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {inv.status && (
                      <span className="text-[11px] text-slate-500 capitalize">
                        {STATUS_LABEL[inv.status] || inv.status}
                      </span>
                    )}
                    {inv.is_overdue && (
                      <span className="text-[11px] font-semibold text-red-600 bg-red-50 border border-red-200 rounded px-1.5 py-0.5 flex items-center gap-1">
                        <AlertCircle className="h-2.5 w-2.5 shrink-0" />
                        Overdue
                      </span>
                    )}
                  </div>
                  {inv.due_date && (
                    <p className="text-xs text-slate-400">Due {fmtDate(inv.due_date)}</p>
                  )}
                  <div className="flex items-center gap-1.5 text-[11px] text-slate-400 mt-0.5" data-testid={`live-ops-invoice-last-nudged-${card.rawId}`}>
                    <Send className="h-3 w-3 shrink-0" />
                    {inv.last_nudged_at
                      ? `Last nudged ${fmtDate(inv.last_nudged_at)}`
                      : "Never nudged"}
                  </div>
                </>
              );
            })()}
          </div>
        ) : card.type === "pending_activation" ? (
          <div className="space-y-1.5" data-testid={`live-ops-activation-body-${card.rawId}`}>
            <p className="text-sm font-semibold text-slate-800 line-clamp-2">
              {card.raw.service_name}
            </p>
            {card.raw.engagement_number && (
              <p className="text-xs text-slate-500">{card.raw.engagement_number}</p>
            )}
            {card.raw.days_since_signed != null && (
              <div className="flex items-center gap-1.5 text-xs text-amber-600 mt-1">
                <Clock className="h-3 w-3 shrink-0" />
                Signed {card.raw.days_since_signed === 0 ? "today" : `${card.raw.days_since_signed}d ago`} — awaiting activation
              </div>
            )}
          </div>
        ) : card.type === "portal_activated" ? (
          <div className="space-y-1.5" data-testid={`live-ops-portal-activated-body-${card.rawId}`}>
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="h-4 w-4 text-emerald-600" />
              </div>
              <p className="text-sm font-semibold text-emerald-800 leading-tight" data-testid={`live-ops-portal-activated-name-${card.rawId}`}>
                Secure Portal access successfully established
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap mt-1">
              {card.lifecycleType && (
                <span className="inline-flex items-center text-[11px] font-medium text-slate-600 bg-slate-100 rounded px-1.5 py-0.5" data-testid={`live-ops-portal-activated-type-${card.rawId}`}>
                  {card.lifecycleType}
                </span>
              )}
              {card.teamLabel && (
                <span className="inline-flex items-center text-[11px] font-medium text-blue-700 bg-blue-50 rounded px-1.5 py-0.5" data-testid={`live-ops-portal-activated-team-${card.rawId}`}>
                  {card.teamLabel}
                </span>
              )}
            </div>
          </div>
        ) : card.type === "production_ready" ? (
          <div className="space-y-1.5" data-testid={`live-ops-production-body-${card.rawId}`}>
            <p className="text-sm font-semibold text-slate-800 line-clamp-2"
              data-testid={`live-ops-production-scope-${card.rawId}`}>
              {card.raw.scope_label || card.raw.service_name}
            </p>
            {card.raw.engagement_number && (
              <p className="text-xs text-slate-400 tabular-nums">
                {card.raw.engagement_number}
              </p>
            )}
            <div className="space-y-0.5 mt-1">
              {[
                "Estimate accepted",
                "Engagement signed",
                "Required payment satisfied",
              ].map((label) => (
                <div key={label} className="flex items-center gap-1.5 text-xs text-emerald-700">
                  <CheckCircle2 className="h-3 w-3 text-emerald-500 shrink-0" />
                  {label}
                </div>
              ))}
            </div>
            <div className="flex items-center gap-1.5 mt-1.5 pt-1.5 border-t border-slate-100">
              <span className="text-[11px] text-slate-400">Organizer:</span>
              <span className={cn("text-[11px] font-medium",
                card.raw.organizer_status === "completed" ? "text-emerald-600" : "text-slate-500")}>
                {card.raw.organizer_status === "completed"
                  ? "Complete"
                  : card.raw.organizer_status === "in_progress"
                  ? `In Progress${card.raw.organizer_pct ? ` · ${card.raw.organizer_pct}%` : ""}`
                  : "Not Started"}
              </span>
            </div>
          </div>
        ) : card.type === "str_active" ? (
          <div className="space-y-1.5" data-testid={`live-ops-str-active-body-${card.rawId}`}>
            <p className="text-sm font-semibold text-slate-800 line-clamp-2"
              data-testid={`live-ops-str-scope-${card.rawId}`}>
              {card.raw.scope_label || "Strategic Tax Review"}
            </p>
            {card.raw.str_state_label && (
              <div className="flex items-center gap-1.5 text-xs text-violet-700 mt-0.5">
                <BookOpen className="h-3 w-3 shrink-0" />
                {card.raw.str_state_label}
              </div>
            )}
            {card.raw.engagement_number && (
              <p className="text-xs text-slate-500">{card.raw.engagement_number}</p>
            )}
            {card.raw.organizer_waiting_days != null && (
              <div className="flex items-center gap-1.5 text-xs text-amber-600 mt-1">
                <Clock className="h-3 w-3 shrink-0" />
                Organizer sent {card.raw.organizer_waiting_days}d ago — awaiting client
              </div>
            )}
          </div>
        ) : card.type === "lifecycle_item" ? (
          <div className="space-y-1.5" data-testid={`live-ops-lifecycle-body-${card.rawId}`}>
            <p className="text-sm font-semibold text-slate-800 line-clamp-2"
              data-testid={`live-ops-lifecycle-state-${card.rawId}`}>
              {card.raw?.current_state_label}
            </p>
            {card.raw?.scope_label && (
              <p className="text-xs text-slate-500 truncate" data-testid={`live-ops-lifecycle-scope-${card.rawId}`}>
                {card.raw.scope_label}
                {card.raw.engagement_number && (
                  <span className="text-slate-400 ml-1 tabular-nums">· {card.raw.engagement_number}</span>
                )}
              </p>
            )}
            {!card.raw?.scope_label && card.raw?.scope_type && card.raw.scope_type !== "client" && card.raw?.scope_id && (
              <p className="text-xs text-slate-400 capitalize">
                {card.raw.scope_type.replace(/_/g, " ")}
              </p>
            )}
          </div>
        ) : card.meetingTitle ? (
          // Zoom card: show meeting title and optional intake reason
          <div className="space-y-1">
            <p className="text-sm font-semibold text-slate-800 line-clamp-2" data-testid={`live-ops-zoom-title-${card.rawId}`}>
              {card.meetingTitle}
            </p>
            {card.intakeReason && (
              <p className="text-xs text-slate-500 line-clamp-1" data-testid={`live-ops-zoom-reason-${card.rawId}`}>
                {card.intakeReason}
              </p>
            )}
          </div>
        ) : (
          <p className="text-sm text-slate-600 line-clamp-2">{card.subtitle || card.title}</p>
        )}
        <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
          <Clock className="h-3 w-3" />{card.timeAgo}
        </p>
      </div>

      {/* Next action */}
      <div className="px-4 pb-3">
        <p className="text-[10px] font-semibold uppercase tracking-[0.07em] text-slate-400 mb-0.5">Next Action</p>
        <p className="text-xs text-slate-700 font-medium" data-testid={`live-ops-next-action-${card.rawId}`}>
          {card.type === "production_ready"
            ? (card.raw.is_str
                ? (card.raw.str_next_action_label || "Begin Strategic Tax Review")
                : "Begin Production")
            : card.type === "str_active"
            ? (card.raw.str_next_action_label || "Open Strategic Review Workspace")
            : card.type === "pending_activation"
            ? (card.raw.is_str ? "Activate STR engagement" : "Activate engagement")
            : card.type === "pending_payment_report"
            ? "Verify payment received — post against invoice"
            : card.type === "outstanding_invoice"
            ? "Waiting for client payment"
            : card.type === "zoom_review"
            ? (card.clientId
                ? "Review meeting analysis"
                : "Identify client / lead for this meeting")
            : card.type === "follow_up_due"
            ? (card.nextActionLabel || "Reconnect with Lead")
            : card.type === "lifecycle_item"
            ? (card.raw?.next_action_label || "Open Workspace")
            : def.nextAction}
        </p>
      </div>

      {/* Action row — sits just above the assignment footer */}
      {card.type === "outstanding_invoice" ? (
        <div className="px-4 pb-3 flex items-center justify-end gap-2">
          <button
            onClick={() => card.clientId && navigate(`/client-workspace/${card.clientId}`)}
            disabled={!card.clientId}
            data-testid={`live-ops-invoice-open-workspace-${card.rawId}`}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700 border border-slate-200 hover:border-slate-300 rounded-lg px-3 py-1.5 transition-colors disabled:opacity-40"
          >
            Open Workspace
          </button>
          <button
            onClick={() => setShowNudgeDialog(true)}
            data-testid={`live-ops-invoice-nudge-${card.rawId}`}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#2F80ED] hover:text-white hover:bg-[#2F80ED] border border-[#2F80ED]/40 hover:border-[#2F80ED] rounded-lg px-3 py-1.5 transition-colors"
          >
            <Send className="h-3.5 w-3.5" />
            Nudge Client
          </button>
        </div>
      ) : card.type === "portal_activated" ? (
        <div className="px-4 pb-3 flex items-center justify-end gap-2">
          <button
            onClick={async () => {
              try {
                await api.post(`/portal-activations/${card.rawId}/acknowledge`);
                onAssigned?.();
              } catch (_) { /* ignore */ }
            }}
            data-testid={`live-ops-portal-mark-reviewed-${card.rawId}`}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700 border border-slate-200 hover:border-slate-300 rounded-lg px-3 py-1.5 transition-colors"
          >
            Mark Reviewed
          </button>
          <button
            onClick={handleAction}
            data-testid={`live-ops-portal-open-${card.rawId}`}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600 hover:text-white hover:bg-emerald-600 border border-emerald-400/50 hover:border-emerald-600 rounded-lg px-3 py-1.5 transition-colors"
          >
            {card.lifecycleType === "Lead" ? "Open Lead" : "Open Workspace"}
            {" "}<ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <div className="px-4 pb-3 flex justify-end">
          <button onClick={handleAction} data-testid={`live-ops-card-action-${card.rawId}`}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#2F80ED] hover:text-white hover:bg-[#2F80ED] border border-[#2F80ED]/40 hover:border-[#2F80ED] rounded-lg px-3 py-1.5 transition-colors">
            {card.type === "production_ready"
              ? (card.raw.is_str ? "Open STR Workspace" : "Open Client Workspace")
              : card.type === "str_active"
              ? "Open STR Workspace"
              : card.type === "lifecycle_item"
              ? "Open Workspace"
              : def.actionLabel}
            {" "}<ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Assignment footer — prominent ownership indicator */}
      <div className={cn("border-t border-slate-100 px-4 py-3 rounded-b-xl", st.bg + "/40")}>
        {card.type === "production_ready"
          ? <ProductionReadyFooter card={card} />
          : card.type === "str_active"
          ? <StrActiveFooter card={card} />
          : card.type === "pending_activation"
          ? <PendingActivationFooter card={card} />
          : card.type === "pending_payment_report"
          ? <PendingPaymentReportFooter card={card} />
          : card.type === "lifecycle_item"
          ? <LiveOpsLifecycleFooter card={card} />
          : card.type === "outstanding_invoice"
          ? <LiveOpsLifecycleFooter card={{ ...card, raw: { ...card.raw, responsibility: "WAITING_ON_CLIENT" } }} />
          : card.type === "portal_activated"
          ? (
            <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
              <ShieldCheck className="h-3 w-3 text-emerald-500 shrink-0" />
              <span className="font-medium">RESPONSIBILITY</span>
              <span className="text-slate-400 ml-0.5">Staff</span>
            </div>
          )
          : <CardAssignmentFooter
              card={card}
              staffList={staffList}
              isManagement={isManagement}
              onAssigned={onAssigned}
            />}
      </div>

      {/* Nudge Client — confirmation dialog (outstanding_invoice cards only) */}
      {showNudgeDialog && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in duration-150"
          onClick={() => !nudgeBusy && setShowNudgeDialog(false)}
          data-testid={`live-ops-nudge-dialog-${card.rawId}`}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-sm mx-4 p-6 animate-in zoom-in-95 duration-150"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="h-10 w-10 rounded-xl bg-amber-50 border border-amber-100 flex items-center justify-center">
                <Send className="h-5 w-5 text-amber-600" />
              </div>
              <button
                onClick={() => setShowNudgeDialog(false)}
                disabled={nudgeBusy}
                className="text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-30"
                data-testid={`live-ops-nudge-close-${card.rawId}`}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <h3 className="text-base font-bold text-slate-900 mb-1">Send payment reminder?</h3>
            <p className="text-sm font-semibold text-slate-700 mb-0.5">{card.clientName}</p>
            {card.raw?.client_number && (
              <p className="text-xs text-slate-400 mb-1">{card.raw.client_number}</p>
            )}
            <div className="bg-slate-50 rounded-xl border border-slate-100 px-3 py-2.5 mb-4 space-y-1">
              {card.raw?.invoice_number && (
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-500">Invoice</span>
                  <span className="text-xs font-mono text-slate-700">{card.raw.invoice_number}</span>
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-xs text-slate-500">Outstanding</span>
                <span className="text-xs font-semibold text-slate-800" data-testid={`live-ops-nudge-balance-${card.rawId}`}>
                  {(() => {
                    try { return new Intl.NumberFormat("en-US", { style: "currency", currency: card.raw?.currency || "USD" }).format(Number(card.raw?.balance || 0)); }
                    catch { return `${card.raw?.currency || "USD"} ${Number(card.raw?.balance || 0).toFixed(2)}`; }
                  })()}
                </span>
              </div>
              {card.raw?.is_overdue && (
                <div className="flex items-center gap-1.5 text-xs text-red-600">
                  <AlertCircle className="h-3 w-3 shrink-0" />
                  Invoice is overdue
                </div>
              )}
            </div>
            <p className="text-sm text-slate-500 mb-5 leading-relaxed">
              A payment reminder email will be sent to the client's registered email address.
              The existing payment reminder template and portal billing link will be used.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setShowNudgeDialog(false)}
                disabled={nudgeBusy}
                className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50"
                data-testid={`live-ops-nudge-cancel-${card.rawId}`}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setNudgeBusy(true);
                  try {
                    await api.post(`/finance/invoices/${card.rawId}/remind`);
                    toast.success(`Payment reminder sent to ${card.clientName}`);
                    setShowNudgeDialog(false);
                    onRefresh();
                  } catch (e) {
                    const detail = e.response?.data?.detail;
                    const status = e.response?.status;
                    if (status === 403) {
                      toast.error("You don't have permission to send reminders.");
                    } else if (status === 422 || status === 400) {
                      toast.error(typeof detail === "string" ? detail : "Cannot send reminder for this invoice.");
                    } else {
                      toast.error(typeof detail === "string" ? detail : "Failed to send reminder. Please try again.");
                    }
                  } finally {
                    setNudgeBusy(false);
                  }
                }}
                disabled={nudgeBusy}
                className="px-4 py-2 text-sm rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-medium transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                data-testid={`live-ops-nudge-confirm-${card.rawId}`}
              >
                {nudgeBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Send Reminder
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Activate Engagement — confirmation dialog (pending_activation cards only) */}
      {showActivateDialog && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in duration-150"
          onClick={() => !activateBusy && setShowActivateDialog(false)}
          data-testid={`live-ops-activate-dialog-${card.rawId}`}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-sm mx-4 p-6 animate-in zoom-in-95 duration-150"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="h-10 w-10 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                <Zap className="h-5 w-5 text-emerald-600" />
              </div>
              <button onClick={() => setShowActivateDialog(false)} disabled={activateBusy} className="text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-30">
                <X className="h-4 w-4" />
              </button>
            </div>
            <h3 className="text-base font-bold text-slate-900 mb-1">Activate engagement?</h3>
            <p className="text-sm font-medium text-slate-700 mb-0.5">{card.raw.service_name}</p>
            {card.raw.engagement_number && (
              <p className="text-xs text-slate-400 mb-3">{card.raw.engagement_number} · {card.clientName}</p>
            )}
            <p className="text-sm text-slate-500 mb-5 leading-relaxed">
              This activates the engagement through Client Success
              {card.raw.is_str ? " and assigns the STR Organizer" : ""}.
              The engagement letter has already been signed.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setShowActivateDialog(false)}
                disabled={activateBusy}
                className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50"
                data-testid={`live-ops-activate-cancel-${card.rawId}`}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setActivateBusy(true);
                  try {
                    await api.post(`/client-success/engagements/${card.rawId}/activate`);
                    toast.success(`Engagement activated for ${card.clientName}`);
                    setShowActivateDialog(false);
                    onRefresh();
                  } catch (e) {
                    const status = e.response?.status;
                    if (status === 409) {
                      // Already activated — treat as success, refresh so card clears
                      toast.info("Engagement is already activated — refreshing");
                      setShowActivateDialog(false);
                      onRefresh();
                    } else {
                      toast.error(e.response?.data?.detail || "Activation failed. Please try again.");
                    }
                  } finally {
                    setActivateBusy(false);
                  }
                }}
                disabled={activateBusy}
                className="px-4 py-2 text-sm rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-medium transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                data-testid={`live-ops-activate-confirm-${card.rawId}`}
              >
                {activateBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
                Activate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Archive / Not Client Related — confirmation dialog */}
      {showArchiveDialog && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in duration-150"
          onClick={() => setShowArchiveDialog(false)}
          data-testid={`live-ops-archive-dialog-${card.rawId}`}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-sm mx-4 p-6 animate-in zoom-in-95 duration-150"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="h-10 w-10 rounded-xl bg-amber-50 border border-amber-100 flex items-center justify-center">
                <Archive className="h-5 w-5 text-amber-600" />
              </div>
              <button onClick={() => setShowArchiveDialog(false)} className="text-slate-400 hover:text-slate-600 transition-colors">
                <X className="h-4 w-4" />
              </button>
            </div>
            <h3 className="text-base font-bold text-slate-900 mb-2">Archive this meeting review?</h3>
            <p className="text-sm text-slate-500 mb-5 leading-relaxed">
              This meeting will be removed from Live Operations and will no longer require assignment or review.
              The meeting record will be retained for audit purposes.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setShowArchiveDialog(false)}
                disabled={archiveBusy}
                className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50"
                data-testid={`live-ops-archive-cancel-${card.rawId}`}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setArchiveBusy(true);
                  try {
                    await api.post(`/meeting-transcripts/${card.rawId}/archive`, { reason: "not_client_related" });
                    toast.success("Meeting archived — removed from Live Operations");
                    setShowArchiveDialog(false);
                    onRefresh();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Archive failed. Please try again.");
                  } finally {
                    setArchiveBusy(false);
                  }
                }}
                disabled={archiveBusy}
                className="px-4 py-2 text-sm rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-medium transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                data-testid={`live-ops-archive-confirm-${card.rawId}`}
              >
                {archiveBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Archive className="h-4 w-4" />}
                Archive Meeting
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Task confirmation dialog (assigned_task cards) */}
      {showDeleteTaskDialog && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in duration-150"
          onClick={() => !deleteTaskBusy && setShowDeleteTaskDialog(false)}
          data-testid={`live-ops-delete-task-dialog-${card.rawId}`}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-sm mx-4 p-6 animate-in zoom-in-95 duration-150"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="h-10 w-10 rounded-xl bg-red-50 border border-red-100 flex items-center justify-center">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <button
                onClick={() => setShowDeleteTaskDialog(false)}
                disabled={deleteTaskBusy}
                className="text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-30"
                data-testid={`live-ops-delete-task-close-${card.rawId}`}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <h3 className="text-base font-bold text-slate-900 mb-1">Delete this assigned task?</h3>
            <p className="text-sm font-semibold text-slate-700 mb-0.5">{card.raw?.title || "Task"}</p>
            {card.clientName && (
              <p className="text-xs text-slate-400 mb-3">{card.clientName}{card.clientNumber ? ` · ${card.clientNumber}` : ""}</p>
            )}
            <p className="text-sm text-slate-500 mb-5 leading-relaxed">
              This will remove it from active work queues but preserve the record for history.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setShowDeleteTaskDialog(false)}
                disabled={deleteTaskBusy}
                className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50"
                data-testid={`live-ops-delete-task-cancel-${card.rawId}`}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setDeleteTaskBusy(true);
                  try {
                    const res = await api.delete(`/tasks/${card.rawId}`);
                    const body = res.data || {};
                    if (body.status === "already_deleted") {
                      toast.info("Task was already deleted — refreshing");
                    } else {
                      toast.success("Task deleted — removed from work queue");
                    }
                    setShowDeleteTaskDialog(false);
                    onRefresh();
                  } catch (e) {
                    const status = e.response?.status;
                    const detail = e.response?.data?.detail;
                    if (status === 404 && detail?.status === "not_found") {
                      // Task doesn't exist in the system — record mismatch, not "already deleted"
                      toast.error("Task record not found. It may have been removed by an administrator.");
                    } else if (status === 404) {
                      toast.error("Task not found — refresh the page and try again.");
                    } else {
                      toast.error((typeof detail === "string" ? detail : null) || "Delete failed. Please try again.");
                    }
                  } finally {
                    setDeleteTaskBusy(false);
                  }
                }}
                disabled={deleteTaskBusy}
                className="px-4 py-2 text-sm rounded-lg bg-red-600 hover:bg-red-700 text-white font-medium transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                data-testid={`live-ops-delete-task-confirm-${card.rawId}`}
              >
                {deleteTaskBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                Delete Task
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Follow Up Later dialog (zoom_review cards) */}
      {showFollowUpDialog && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in duration-150"
          onClick={() => !fuBusy && setShowFollowUpDialog(false)}
          data-testid={`live-ops-followup-dialog-${card.rawId}`}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-sm mx-4 p-6 animate-in zoom-in-95 duration-150"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="h-10 w-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center">
                <Calendar className="h-5 w-5 text-blue-600" />
              </div>
              <button onClick={() => setShowFollowUpDialog(false)} disabled={fuBusy} className="text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-30">
                <X className="h-4 w-4" />
              </button>
            </div>
            <h3 className="text-base font-bold text-slate-900 mb-1">Follow Up Later</h3>
            <p className="text-sm text-slate-500 mb-4 leading-relaxed">
              This card will be removed from Live Operations until the follow-up date. The meeting, transcript, analysis, and any Consultation Summary remain intact.
            </p>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  Follow-Up Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={fuDate}
                  min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                  onChange={(e) => setFuDate(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  data-testid={`live-ops-followup-date-${card.rawId}`}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Reason</label>
                <select
                  value={fuReason}
                  onChange={(e) => setFuReason(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
                  data-testid={`live-ops-followup-reason-${card.rawId}`}
                >
                  {REASON_OPTIONS.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Note (optional)</label>
                <textarea
                  value={fuNote}
                  onChange={(e) => setFuNote(e.target.value)}
                  rows={2}
                  placeholder="Internal context about this future opportunity..."
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 resize-none"
                  data-testid={`live-ops-followup-note-${card.rawId}`}
                />
              </div>
            </div>

            <div className="flex gap-2 justify-end mt-5">
              <button
                onClick={() => setShowFollowUpDialog(false)}
                disabled={fuBusy}
                className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50"
                data-testid={`live-ops-followup-cancel-${card.rawId}`}
              >
                Cancel
              </button>
              <button
                onClick={handleFollowUpLater}
                disabled={fuBusy || !fuDate}
                className="px-4 py-2 text-sm rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                data-testid={`live-ops-followup-save-${card.rawId}`}
              >
                {fuBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calendar className="h-4 w-4" />}
                Save Follow-Up
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EmptyState({ hasFilters }) {
  return (
    <div className="col-span-full flex flex-col items-center justify-center py-20 text-center" data-testid="live-ops-empty">
      <div className="h-16 w-16 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center mb-4">
        <CheckCircle2 className="h-8 w-8 text-emerald-500" />
      </div>
      <h3 className="text-lg font-bold text-slate-900 mb-1">
        {hasFilters ? "No items match your filters" : "All caught up"}
      </h3>
      <p className="text-sm text-slate-500 max-w-xs">
        {hasFilters
          ? "Try adjusting your filters to see more items."
          : "No pending operational items. The queue refreshes automatically every 20 seconds."}
      </p>
    </div>
  );
}

// ── KPI Card ────────────────────────────────────────────────────────────────
const KPI_COLORS = {
  orange: {
    border:    "border-amber-200",
    hoverBorder:"hover:border-amber-400",
    activeBg:  "bg-amber-500 border-amber-500",
    inactiveBg:"bg-white hover:bg-amber-50",
    ring:      "ring-amber-300",
    count:     { active: "text-white", inactive: "text-amber-600"  },
    label:     { active: "text-white/90", inactive: "text-amber-800" },
    sub:       { active: "text-white/70", inactive: "text-amber-500" },
    strip:     "bg-amber-500",
  },
  blue: {
    border:    "border-blue-200",
    hoverBorder:"hover:border-blue-400",
    activeBg:  "bg-blue-500 border-blue-500",
    inactiveBg:"bg-white hover:bg-blue-50",
    ring:      "ring-blue-300",
    count:     { active: "text-white", inactive: "text-blue-600"  },
    label:     { active: "text-white/90", inactive: "text-blue-800" },
    sub:       { active: "text-white/70", inactive: "text-blue-500" },
    strip:     "bg-blue-500",
  },
  purple: {
    border:    "border-violet-200",
    hoverBorder:"hover:border-violet-400",
    activeBg:  "bg-violet-500 border-violet-500",
    inactiveBg:"bg-white hover:bg-violet-50",
    ring:      "ring-violet-300",
    count:     { active: "text-white", inactive: "text-violet-600"  },
    label:     { active: "text-white/90", inactive: "text-violet-800" },
    sub:       { active: "text-white/70", inactive: "text-violet-500" },
    strip:     "bg-violet-500",
  },
  red: {
    border:    "border-red-200",
    hoverBorder:"hover:border-red-400",
    activeBg:  "bg-red-500 border-red-500",
    inactiveBg:"bg-white hover:bg-red-50",
    ring:      "ring-red-300",
    count:     { active: "text-white", inactive: "text-red-600"  },
    label:     { active: "text-white/90", inactive: "text-red-800" },
    sub:       { active: "text-white/70", inactive: "text-red-500" },
    strip:     "bg-red-500",
  },
  rose: {
    border:    "border-rose-200",
    hoverBorder:"hover:border-rose-400",
    activeBg:  "bg-rose-600 border-rose-600",
    inactiveBg:"bg-white hover:bg-rose-50",
    ring:      "ring-rose-300",
    count:     { active: "text-white", inactive: "text-rose-600"  },
    label:     { active: "text-white/90", inactive: "text-rose-800" },
    sub:       { active: "text-white/70", inactive: "text-rose-500" },
    strip:     "bg-rose-600",
  },
  slate: {
    border:    "border-slate-200",
    hoverBorder:"hover:border-slate-400",
    activeBg:  "bg-slate-800 border-slate-800",
    inactiveBg:"bg-white hover:bg-slate-50",
    ring:      "ring-slate-300",
    count:     { active: "text-white", inactive: "text-slate-700"  },
    label:     { active: "text-white/90", inactive: "text-slate-800" },
    sub:       { active: "text-white/70", inactive: "text-slate-500" },
    strip:     "bg-slate-800",
  },
};

function KpiCard({ label, sublabel, count, color, active, onClick, testid, dimmed }) {
  const cfg = KPI_COLORS[color];
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={cn(
        "rounded-2xl border-2 px-4 py-4 flex flex-col gap-1 text-left cursor-pointer",
        "transition-all duration-150 hover:-translate-y-0.5 hover:shadow-md active:translate-y-0",
        active
          ? cn(cfg.activeBg, "shadow-md ring-4", cfg.ring)
          : cn(cfg.border, cfg.hoverBorder, cfg.inactiveBg),
        dimmed && !active && "opacity-40"
      )}
    >
      <span className={cn("text-3xl font-black tracking-tight tabular-nums leading-none",
        active ? cfg.count.active : cfg.count.inactive)}>
        {count ?? "—"}
      </span>
      <span className={cn("text-sm font-bold leading-tight mt-1",
        active ? cfg.label.active : cfg.label.inactive)}>
        {label}
      </span>
      <span className={cn("text-[11px] leading-snug",
        active ? cfg.sub.active : cfg.sub.inactive)}>
        {sublabel}
      </span>
    </button>
  );
}

// ── Responsibility sections config ─────────────────────────────────────────
const RESPONSIBILITY_SECTIONS = [
  {
    key:    "STAFF",
    label:  "Waiting on Staff",
    desc:   "Items that require action from our team",
    color:  "orange",
    testid: "section-waiting-on-staff",
  },
  {
    key:    "CLIENT",
    label:  "Waiting on Client",
    desc:   "Items where the client needs to take action",
    color:  "blue",
    testid: "section-waiting-on-client",
  },
  {
    key:    "THIRD_PARTY",
    label:  "Waiting on Third Party",
    desc:   "Items where we are waiting on an external party",
    color:  "purple",
    testid: "section-waiting-on-third-party",
  },
];

const SECTION_COLOR = {
  orange: {
    bg:     "bg-amber-50",
    border: "border-amber-200",
    text:   "text-amber-800",
    count:  "bg-amber-100 text-amber-700",
    strip:  "bg-amber-500",
    hover:  "hover:bg-amber-100/60",
  },
  blue: {
    bg:     "bg-blue-50",
    border: "border-blue-200",
    text:   "text-blue-800",
    count:  "bg-blue-100 text-blue-700",
    strip:  "bg-blue-500",
    hover:  "hover:bg-blue-100/60",
  },
  purple: {
    bg:     "bg-violet-50",
    border: "border-violet-200",
    text:   "text-violet-800",
    count:  "bg-violet-100 text-violet-700",
    strip:  "bg-violet-500",
    hover:  "hover:bg-violet-100/60",
  },
};

function ResponsibilitySectionHeader({ section, count, collapsed, onToggle }) {
  const sc = SECTION_COLOR[section.color];
  return (
    <button
      onClick={onToggle}
      className={cn(
        "w-full flex items-center gap-3 mb-4 rounded-xl border px-4 py-3 text-left",
        "transition-colors group",
        sc.bg, sc.border, sc.hover
      )}
      data-testid={`${section.testid}-header`}
      aria-expanded={!collapsed}
    >
      <div className={cn("h-5 w-1.5 rounded-full shrink-0", sc.strip)} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={cn("text-xs font-bold uppercase tracking-[0.09em]", sc.text)}>
            {section.label}
          </span>
          <span className={cn("text-[11px] font-bold rounded-full px-2 py-0.5", sc.count)}>
            {count}
          </span>
        </div>
        <p className="text-[11px] text-slate-500 mt-0.5">{section.desc}</p>
      </div>
      <ChevronDown className={cn(
        "h-4 w-4 text-slate-400 transition-transform duration-150 shrink-0",
        collapsed && "-rotate-90"
      )} />
    </button>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────
// ── Phase 4: Lifecycle Operations Management Panel ─────────────────────────

function HealthBadge({ count, label, color }) {
  const colors = {
    blue:   "bg-blue-50 text-blue-700 border-blue-200",
    amber:  "bg-amber-50 text-amber-700 border-amber-200",
    red:    "bg-red-50 text-red-700 border-red-200",
    green:  "bg-green-50 text-green-700 border-green-200",
    slate:  "bg-slate-50 text-slate-600 border-slate-200",
  };
  return (
    <div className={cn("rounded-lg border px-3 py-2 text-center min-w-[80px]", colors[color] || colors.slate)}>
      <div className="text-xl font-bold tabular-nums">
        {count == null ? <span className="text-base text-inherit opacity-50">—</span> : count}
      </div>
      <div className="text-[10px] font-medium uppercase tracking-wide opacity-70 mt-0.5 leading-tight">{label}</div>
    </div>
  );
}

function IncidentRow({ incident, userRole, onAck, onRetry, actionLoading }) {
  const isAdmin   = userRole === "administrator";
  const isPartner = userRole === "partner";
  const canAck    = isAdmin || isPartner;
  const canRetry  = isAdmin;
  const loadKey   = incident.id;

  const statusBadge = (() => {
    if (incident.resolved)     return <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">Resolved</span>;
    if (incident.acknowledged) return <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Acknowledged</span>;
    return <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700">Unresolved</span>;
  })();

  return (
    <div
      data-testid={`lifecycle-incident-row-${incident.id}`}
      className="flex items-start gap-3 py-2.5 border-b border-slate-100 last:border-b-0"
    >
      <AlertOctagon className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold text-slate-800 truncate">
            {incident.client_id || "—"}
          </span>
          {statusBadge}
          <span className="text-[10px] text-slate-400 ml-auto">
            {incident.attempt_count != null ? `${incident.attempt_count} attempts` : ""}
          </span>
        </div>
        <p className="text-[11px] text-slate-500 truncate mt-0.5">
          {incident.last_error || incident.trigger_event || "Terminal failure"}
        </p>
        <p className="text-[10px] text-slate-400 mt-0.5">
          {incident.created_at
            ? new Date(incident.created_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })
            : ""}
        </p>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        {canAck && !incident.acknowledged && (
          <button
            data-testid={`lifecycle-ack-btn-${incident.id}`}
            disabled={actionLoading[loadKey]}
            onClick={() => onAck(incident.id)}
            className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-blue-200 text-blue-700 hover:bg-blue-50 disabled:opacity-50 transition-colors"
            title="Acknowledge"
          >
            {actionLoading[loadKey] === "ack"
              ? <Loader2 className="h-3 w-3 animate-spin" />
              : <Bell className="h-3 w-3" />}
            Ack
          </button>
        )}
        {canRetry && !incident.retry_requested && (
          <button
            data-testid={`lifecycle-retry-btn-${incident.id}`}
            disabled={actionLoading[loadKey]}
            onClick={() => onRetry(incident.id)}
            className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-amber-200 text-amber-700 hover:bg-amber-50 disabled:opacity-50 transition-colors"
            title="Retry"
          >
            {actionLoading[loadKey] === "retry"
              ? <Loader2 className="h-3 w-3 animate-spin" />
              : <RotateCcw className="h-3 w-3" />}
            Retry
          </button>
        )}
        {incident.retry_requested && (
          <span className="text-[10px] text-amber-600 font-medium">Retry queued</span>
        )}
      </div>
    </div>
  );
}

function LifecycleOpsPanel({ userRole }) {
  const [collapsed,     setCollapsed]     = useState(false);
  const [health,        setHealth]        = useState(null);
  const [unresolvedInc, setUnresolvedInc] = useState([]);
  const [pendingRetries,setPendingRetries]= useState([]);
  const [ackedInc,      setAckedInc]      = useState([]);
  const [resolvedInc,   setResolvedInc]   = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [error,         setError]         = useState(null);
  const [activeTab,     setActiveTab]     = useState("critical"); // critical|pending|acknowledged|resolved

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [healthRes, critRes, pendRes, ackRes, resRes] = await Promise.all([
        getLifecycleQueueHealth(),
        getOperationalIncidents({ status: "unresolved" }),
        getOperationalIncidents({ status: "unresolved", type: "lifecycle_reconciliation_terminal_failure" }),
        getOperationalIncidents({ status: "acknowledged" }),
        getOperationalIncidents({ status: "resolved", limit: 20 }),
      ]);
      setHealth(healthRes.data);
      setUnresolvedInc(critRes.data?.incidents || []);
      setPendingRetries(pendRes.data?.incidents?.filter((i) => i.retry_requested) || []);
      setAckedInc(ackRes.data?.incidents || []);
      setResolvedInc(resRes.data?.incidents || []);
    } catch (e) {
      setError("Unable to load lifecycle operations data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleAck = async (incidentId) => {
    setActionLoading((prev) => ({ ...prev, [incidentId]: "ack" }));
    try {
      await acknowledgeIncident(incidentId);
      toast.success("Incident acknowledged");
      await refresh();
    } catch {
      toast.error("Failed to acknowledge incident");
    } finally {
      setActionLoading((prev) => { const n = { ...prev }; delete n[incidentId]; return n; });
    }
  };

  const handleRetry = async (incidentId) => {
    setActionLoading((prev) => ({ ...prev, [incidentId]: "retry" }));
    try {
      await retryIncident(incidentId);
      toast.success("Retry queued — worker will process on next poll");
      await refresh();
    } catch {
      toast.error("Failed to queue retry");
    } finally {
      setActionLoading((prev) => { const n = { ...prev }; delete n[incidentId]; return n; });
    }
  };

  const TABS = [
    { key: "critical",    label: "Critical",    count: unresolvedInc.length,  icon: AlertOctagon },
    { key: "pending",     label: "Retries",     count: pendingRetries.length, icon: RotateCcw },
    { key: "acknowledged",label: "Acknowledged", count: ackedInc.length,      icon: Bell },
    { key: "resolved",    label: "Resolved",    count: resolvedInc.length,    icon: CheckSquare },
  ];

  const tabContent = (() => {
    switch (activeTab) {
      case "critical":    return unresolvedInc;
      case "pending":     return pendingRetries;
      case "acknowledged":return ackedInc;
      case "resolved":    return resolvedInc;
      default:            return [];
    }
  })();

  const hasCritical = unresolvedInc.length > 0;

  return (
    <div
      data-testid="lifecycle-ops-panel"
      className={cn(
        "rounded-xl border bg-white overflow-hidden mb-4",
        hasCritical ? "border-red-200 shadow-sm" : "border-slate-200",
      )}
    >
      {/* Panel header */}
      <button
        onClick={() => setCollapsed((c) => !c)}
        data-testid="lifecycle-ops-panel-toggle"
        className={cn(
          "w-full flex items-center justify-between px-4 py-3 text-left transition-colors",
          hasCritical ? "bg-red-50 hover:bg-red-100" : "bg-slate-50 hover:bg-slate-100",
        )}
      >
        <div className="flex items-center gap-2.5">
          <Database className={cn("h-4 w-4", hasCritical ? "text-red-600" : "text-slate-500")} />
          <span className={cn("text-sm font-semibold", hasCritical ? "text-red-800" : "text-slate-700")}>
            Lifecycle Operations
          </span>
          {hasCritical && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-600 text-white animate-pulse">
              {unresolvedInc.length} CRITICAL
            </span>
          )}
          {loading && <Loader2 className="h-3.5 w-3.5 text-slate-400 animate-spin" />}
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={(e) => { e.stopPropagation(); refresh(); }}
            data-testid="lifecycle-ops-refresh-btn"
            className="text-slate-400 hover:text-slate-600 transition-colors p-1"
            title="Refresh"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
          {collapsed
            ? <ChevronDown className="h-4 w-4 text-slate-400" />
            : <ChevronUp   className="h-4 w-4 text-slate-400" />}
        </div>
      </button>

      {!collapsed && (
        <div className="p-4 space-y-4">
          {error && (
            <div className="flex items-center gap-2 text-red-600 bg-red-50 rounded-lg p-3 text-sm">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {error}
            </div>
          )}

          {/* Queue health summary */}
          {health && (
            <div
              data-testid="lifecycle-queue-health"
              className="grid grid-cols-3 sm:grid-cols-6 gap-2"
            >
              <HealthBadge count={health.pending_ready_count}   label="Ready"     color="blue" />
              <HealthBadge count={health.pending_backoff_count} label="Backoff"   color="amber" />
              <HealthBadge count={health.claimed_count}        label="In Flight" color="slate" />
              <HealthBadge count={health.failed_terminal_count}label="Terminal"  color="red" />
              <HealthBadge count={health.completed_last_24h}   label="Done (24h)"color="green" />
              <HealthBadge
                count={health.oldest_pending_age_s != null
                  ? health.oldest_pending_age_s < 60
                    ? `${health.oldest_pending_age_s}s`
                    : health.oldest_pending_age_s < 3600
                      ? `${Math.round(health.oldest_pending_age_s / 60)}m`
                      : `${(health.oldest_pending_age_s / 3600).toFixed(1)}h`
                  : null}
                label="Oldest Pending"
                color="slate"
              />
            </div>
          )}

          {/* Tabs */}
          <div className="border-b border-slate-200">
            <div className="flex gap-0" role="tablist">
              {TABS.map(({ key, label, count, icon: Icon }) => (
                <button
                  key={key}
                  role="tab"
                  aria-selected={activeTab === key}
                  data-testid={`lifecycle-ops-tab-${key}`}
                  onClick={() => setActiveTab(key)}
                  className={cn(
                    "flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors",
                    activeTab === key
                      ? "border-blue-600 text-blue-700"
                      : "border-transparent text-slate-500 hover:text-slate-700",
                  )}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {label}
                  {count > 0 && (
                    <span className={cn(
                      "text-[10px] px-1.5 py-0.5 rounded-full font-bold",
                      key === "critical" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600",
                    )}>
                      {count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Tab content */}
          <div data-testid={`lifecycle-ops-tabcontent-${activeTab}`}>
            {loading ? (
              <div className="py-6 text-center text-slate-400 text-sm">
                <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
                Loading…
              </div>
            ) : tabContent.length === 0 ? (
              <div className="py-6 text-center text-slate-400 text-sm flex flex-col items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-400" />
                {activeTab === "critical" ? "No unresolved incidents" : "None"}
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {tabContent.map((inc) => (
                  <IncidentRow
                    key={inc.id}
                    incident={inc}
                    userRole={userRole}
                    onAck={handleAck}
                    onRetry={handleRetry}
                    actionLoading={actionLoading}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function LiveOperations() {
  const { user } = useAuth();
  const isManagement = MGMT_ROLES.has(user?.role);
  const sectionRefs  = useRef({});

  // ── Persisted filter state ─────────────────────────────────────────────
  const prefs = loadPrefs();

  const [viewMode,             setViewMode]             = useState(() => isManagement ? (prefs.viewMode       || "all") : "all");
  const [assigneeFilter,       setAssigneeFilter]       = useState(() => isManagement ? (prefs.assigneeFilter || "all") : "all");
  const [responsibilityFilter, setResponsibilityFilter] = useState(() => prefs.responsibilityFilter || "all");
  const [moduleFilter,         setModuleFilter]         = useState(() => prefs.moduleFilter   || "all");
  const [urgentFilter,         setUrgentFilter]         = useState(false);
  const [overdueFilter,        setOverdueFilter]        = useState(false);
  const [searchQuery,          setSearchQuery]          = useState("");
  const [showMoreModules,      setShowMoreModules]      = useState(false);

  useEffect(() => {
    savePrefs({ viewMode, assigneeFilter, responsibilityFilter, moduleFilter });
  }, [viewMode, assigneeFilter, responsibilityFilter, moduleFilter]);

  // ── Data state ─────────────────────────────────────────────────────────
  const [cards,       setCards]       = useState([]);
  const [staffList,   setStaffList]   = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [refreshing,  setRefreshing]  = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const loadRef   = useRef(null);
  const inflightRef = useRef(false);   // P0 Phase 1: prevent overlapping ops-summary requests

  const handleAssigned = useCallback((cardId, staffId, staffName) => {
    setCards((prev) => prev.map((c) =>
      c.id === cardId ? { ...c, assigneeId: staffId, assigneeName: staffName } : c
    ));
    setTimeout(() => loadRef.current?.(true), 900);
  }, []);

  // ── Fetch staff list ───────────────────────────────────────────────────
  useEffect(() => {
    if (!isManagement) return;
    api.get("/notifications/staff-assignees")
      .then((r) => {
        const list = r.data || [];
        setStaffList(list);
        if (prefs.assigneeFilter && prefs.assigneeFilter !== "all" && prefs.assigneeFilter !== "unassigned") {
          if (!list.find((s) => s.id === prefs.assigneeFilter)) setAssigneeFilter("all");
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManagement]);

  // ── Build API params ────────────────────────────────────────────────────
  const fetchParams = useMemo(() => {
    if (!isManagement) return {};
    const p = {};
    if (viewMode === "my_work") {
      p.my_work = "true";
    } else if (assigneeFilter && assigneeFilter !== "all") {
      p.assignee_id = assigneeFilter;
    }
    return p;
  }, [viewMode, assigneeFilter, isManagement]);

  // ── Load / refresh ─────────────────────────────────────────────────────
  const load = useCallback(async (silent = false) => {
    // P0 Phase 1 in-flight guard: skip silent (poll/background) refreshes while a
    // previous ops-summary request is still pending. Initial loads (silent=false)
    // always execute. The finally block always clears the flag, so no permanent block.
    if (silent && inflightRef.current) return;
    inflightRef.current = true;
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const qs  = new URLSearchParams(fetchParams).toString();
      const res = await api.get(`/notifications/ops-summary${qs ? "?" + qs : ""}`);
      const summary = res.data || {};

      const built = [
        ...(summary.messages?.items              || []).map((i) => mapItem("unanswered_message",   i)),
        ...(summary.appointments?.items          || []).map((i) => mapItem("appointment_request",  i)),
        ...(summary.webchat?.items               || []).map((i) => mapItem("webchat_abandoned",    i)),
        ...(summary.zoom?.items                  || []).map((i) => mapItem("zoom_review",          i)),
        ...(summary.follow_up_due?.items         || []).map((i) => mapItem("follow_up_due",        i)),
        ...(summary.pending_payment_reports?.items || []).map((i) => mapItem("pending_payment_report", i)),
        ...(summary.outstanding_invoices?.items  || []).map((i) => mapItem("outstanding_invoice",  i)),
        ...(summary.pending_activation?.items    || []).map((i) => mapItem("pending_activation",   i)),
        ...(summary.production_ready?.items      || []).map((i) => mapItem("production_ready",     i)),
        ...(summary.str_active?.items            || []).map((i) => mapItem("str_active",           i)),
        ...(summary.fbar_ready?.items            || []).map((i) => mapItem("fbar_ready",           i)),
        ...(summary.lifecycle_items?.items       || []).map((i) => mapItem("lifecycle_item",       i)),
        ...(summary.assigned_tasks?.items        || []).map((i) => mapItem("assigned_task",        i)),
        ...(summary.portal_activations?.items    || []).map((i) => mapItem("portal_activated",     i)),
      ];

      setCards(built);
      setLastRefresh(new Date());
    } catch {
      if (!silent) toast.error("Failed to load Live Operations data");
    } finally {
      setLoading(false); setRefreshing(false);
      inflightRef.current = false;   // always clear — prevents permanent block on error
    }
  }, [fetchParams]);

  useEffect(() => { loadRef.current = load; }, [load]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = setInterval(() => load(true), 20000);
    return () => clearInterval(id);
  }, [load]);

  // ── KPI counts — from full unfiltered scope-card set ──────────────────
  const kpiCounts = useMemo(() => ({
    staff:      cards.filter((c) => getCardResponsibility(c) === "STAFF").length,
    client:     cards.filter((c) => getCardResponsibility(c) === "CLIENT").length,
    thirdParty: cards.filter((c) => getCardResponsibility(c) === "THIRD_PARTY").length,
    urgent:     cards.filter((c) => isCardUrgent(c)).length,
    overdue:    cards.filter((c) => isCardOverdue(c)).length,
  }), [cards]);

  // ── Combined filtering (responsibility + cross-cuts + type + search) ───
  const filteredCards = useMemo(() => {
    let r = [...cards];
    // Primary: responsibility bucket
    if (responsibilityFilter !== "all") {
      r = r.filter((c) => getCardResponsibility(c) === responsibilityFilter);
    }
    // Cross-cutting: urgent / overdue
    if (urgentFilter)  r = r.filter((c) => isCardUrgent(c));
    if (overdueFilter) r = r.filter((c) => isCardOverdue(c));
    // Secondary: type filter (from expanded panel)
    if (moduleFilter !== "all") {
      const t = MODULE_OPTIONS.find((m) => m.key === moduleFilter)?.type;
      if (t) r = r.filter((c) => c.type === t);
    }
    // Search
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      r = r.filter((c) =>
        (c.clientName   || "").toLowerCase().includes(q) ||
        (c.raw?.client_number || "").toLowerCase().includes(q) ||
        (c.raw?.invoice_number || "").toLowerCase().includes(q) ||
        (c.raw?.service_name   || "").toLowerCase().includes(q) ||
        (c.title        || "").toLowerCase().includes(q) ||
        (CARD_TYPES[c.type]?.label || "").toLowerCase().includes(q)
      );
    }
    return r;
  }, [cards, responsibilityFilter, urgentFilter, overdueFilter, moduleFilter, searchQuery]);

  // ── Responsibility section grouping ────────────────────────────────────
  const responsibilitySections = useMemo(() => {
    const map = { STAFF: [], CLIENT: [], THIRD_PARTY: [] };
    for (const card of filteredCards) {
      const key = getCardResponsibility(card);
      map[key].push(card);
    }
    return map;
  }, [filteredCards]);

  // ── Module counts (for secondary type filter panel) ────────────────────
  const moduleCounts = useMemo(() => {
    const counts = {};
    for (const c of cards) {
      const def = CARD_TYPES[c.type];
      if (def?.moduleKey) counts[def.moduleKey] = (counts[def.moduleKey] || 0) + 1;
    }
    return counts;
  }, [cards]);

  // ── Section collapse state ─────────────────────────────────────────────
  const LS_COLLAPSE_KEY = "liveops_resp_collapse_v1";
  const [sectionCollapsed, setSectionCollapsed] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_COLLAPSE_KEY)) || {}; } catch { return {}; }
  });
  const toggleSection = useCallback((key) => {
    setSectionCollapsed((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem(LS_COLLAPSE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  // ── KPI card click — filter + scroll ──────────────────────────────────
  const handleKpiClick = useCallback((type) => {
    if (type === "all") {
      setResponsibilityFilter("all");
    } else if (type === "staff") {
      setResponsibilityFilter("STAFF");
      setTimeout(() => sectionRefs.current["STAFF"]?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    } else if (type === "client") {
      setResponsibilityFilter("CLIENT");
      setTimeout(() => sectionRefs.current["CLIENT"]?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    } else if (type === "third_party") {
      setResponsibilityFilter("THIRD_PARTY");
      setTimeout(() => sectionRefs.current["THIRD_PARTY"]?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    } else if (type === "urgent") {
      setUrgentFilter((p) => !p);
      setOverdueFilter(false);
    } else if (type === "overdue") {
      setOverdueFilter((p) => !p);
      setUrgentFilter(false);
    }
  }, []);

  const hasFilters = responsibilityFilter !== "all" || urgentFilter || overdueFilter || moduleFilter !== "all" || !!searchQuery.trim();

  const firstName = (user?.name || "").split(" ")[0] || "there";
  const hour      = new Date().getHours();
  const greeting  = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  return (
    <div className="space-y-5" data-testid="live-operations-page">

      {/* ── Level 0: Page header ────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Activity className="h-5 w-5 text-[#2F80ED]" />
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Live Operations</h1>
            {refreshing && <Loader2 className="h-4 w-4 text-slate-400 animate-spin" />}
          </div>
          <p className="text-sm text-slate-500">
            {greeting}, {firstName}.{" "}
            {isManagement
              ? viewMode === "my_work"
                ? "Showing work assigned to you."
                : assigneeFilter === "unassigned"
                  ? "Showing unassigned items."
                  : assigneeFilter !== "all"
                    ? `Filtered to: ${staffList.find((s) => s.id === assigneeFilter)?.name || "Staff member"}.`
                    : "Firm-wide view."
              : "Here's what needs your attention right now."}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {lastRefresh && (
            <span className="text-xs text-slate-400 hidden sm:inline">
              Updated {timeAgo(lastRefresh.toISOString())}
            </span>
          )}
          <button
            onClick={() => load(true)}
            disabled={refreshing}
            data-testid="live-ops-refresh-btn"
            className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 border border-slate-200 hover:border-slate-300 rounded-lg px-3 py-2 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={cn("h-4 w-4", refreshing && "animate-spin")} />
            Refresh
          </button>
        </div>
      </div>

      {/* ── Phase 4: Lifecycle Operations Management Panel (admin/partner only) ── */}
      {isManagement && (
        <LifecycleOpsPanel userRole={user?.role} />
      )}

      {/* ── Level 1: Responsibility KPI Cards ────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="live-ops-kpi-cards">
        <KpiCard
          label="All"
          sublabel="All responsibilities combined"
          count={loading ? null : cards.length}
          color="slate"
          active={responsibilityFilter === "all"}
          onClick={() => handleKpiClick("all")}
          testid="kpi-all"
        />
        <KpiCard
          label="Waiting on Staff"
          sublabel="Requires action from our team"
          count={loading ? null : kpiCounts.staff}
          color="orange"
          active={responsibilityFilter === "STAFF"}
          onClick={() => handleKpiClick("staff")}
          testid="kpi-waiting-on-staff"
          dimmed={!loading && kpiCounts.staff === 0}
        />
        <KpiCard
          label="Waiting on Client"
          sublabel="Client needs to take action"
          count={loading ? null : kpiCounts.client}
          color="blue"
          active={responsibilityFilter === "CLIENT"}
          onClick={() => handleKpiClick("client")}
          testid="kpi-waiting-on-client"
          dimmed={!loading && kpiCounts.client === 0}
        />
        <KpiCard
          label="Waiting on Third Party"
          sublabel="External party dependency"
          count={loading ? null : kpiCounts.thirdParty}
          color="purple"
          active={responsibilityFilter === "THIRD_PARTY"}
          onClick={() => handleKpiClick("third_party")}
          testid="kpi-waiting-on-third-party"
          dimmed={!loading && kpiCounts.thirdParty === 0}
        />
      </div>

      {/* ── Level 2: Control bar (scope + risk + work type + search) ─── */}
      <div className="bg-white border border-slate-200 rounded-xl px-4 py-3.5 space-y-2.5" data-testid="live-ops-control-bar">

        {/* Row A: Ownership + Risk + Search */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Consolidated ownership selector (management only) */}
          {isManagement && (
            <OwnershipSelector
              viewMode={viewMode}
              assigneeFilter={assigneeFilter}
              staffList={staffList}
              onChangeMode={(m) => { setViewMode(m); if (m === "my_work") setAssigneeFilter("all"); }}
              onChangeAssignee={(v) => setAssigneeFilter(v)}
            />
          )}
          {isManagement && <div className="h-4 w-px bg-slate-200 shrink-0" />}

          {/* Compact risk pills */}
          <CompactRiskPill
            label="Urgent"
            count={loading ? 0 : kpiCounts.urgent}
            active={urgentFilter}
            color="red"
            onClick={() => { setUrgentFilter((f) => !f); setOverdueFilter(false); }}
            testid="risk-pill-urgent"
          />
          <CompactRiskPill
            label="Overdue"
            count={loading ? 0 : kpiCounts.overdue}
            active={overdueFilter}
            color="rose"
            onClick={() => { setOverdueFilter((f) => !f); setUrgentFilter(false); }}
            testid="risk-pill-overdue"
          />

          {/* Search */}
          <div className="flex-1 min-w-[180px] max-w-sm relative ml-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search clients, leads, or items…"
              data-testid="live-ops-search"
              className="w-full pl-9 pr-8 py-1.5 text-sm border border-slate-200 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-300 transition-colors bg-slate-50 focus:bg-white"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Row B: Work Type — primary always visible, secondary under More ▼ */}
        <div className="border-t border-slate-100 pt-2.5" data-testid="type-filter-panel">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[10px] font-semibold uppercase tracking-[0.09em] text-slate-400 shrink-0 mr-0.5">
              Work Type
            </span>
            {MODULE_OPTIONS.filter((m) => PRIMARY_MODULE_KEYS.has(m.key)).map((m) => (
              <button
                key={m.key}
                onClick={() => setModuleFilter(m.key)}
                data-testid={`module-${m.key}`}
                className={cn(
                  "inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium border transition-colors",
                  moduleFilter === m.key
                    ? "bg-slate-800 text-white border-slate-800"
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:text-slate-900"
                )}
              >
                {m.label}
                {m.key !== "all" && moduleCounts[m.key] > 0 && (
                  <span className={cn("rounded-full px-1 py-0.5 text-[10px] font-bold",
                    moduleFilter === m.key ? "bg-white/20" : "bg-slate-100 text-slate-600")}>
                    {moduleCounts[m.key]}
                  </span>
                )}
              </button>
            ))}
            <button
              onClick={() => setShowMoreModules((v) => !v)}
              data-testid="work-type-more-btn"
              className={cn(
                "inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium border transition-colors",
                (showMoreModules || (moduleFilter !== "all" && !PRIMARY_MODULE_KEYS.has(moduleFilter)))
                  ? "bg-slate-800 text-white border-slate-800"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:text-slate-900"
              )}
            >
              {showMoreModules ? "Less" : "More"}
              <ChevronDown className={cn("h-3 w-3 transition-transform", showMoreModules && "rotate-180")} />
            </button>
          </div>

          {/* Secondary work types */}
          {(showMoreModules || (moduleFilter !== "all" && !PRIMARY_MODULE_KEYS.has(moduleFilter))) && (
            <div className="flex flex-wrap gap-1.5 mt-1.5 pl-1">
              {MODULE_OPTIONS.filter((m) => !PRIMARY_MODULE_KEYS.has(m.key)).map((m) => (
                <button
                  key={m.key}
                  onClick={() => setModuleFilter(m.key)}
                  data-testid={`module-${m.key}`}
                  className={cn(
                    "inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium border transition-colors",
                    moduleFilter === m.key
                      ? "bg-slate-800 text-white border-slate-800"
                      : "bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:text-slate-900"
                  )}
                >
                  {m.label}
                  {moduleCounts[m.key] > 0 && (
                    <span className={cn("rounded-full px-1 py-0.5 text-[10px] font-bold",
                      moduleFilter === m.key ? "bg-white/20" : "bg-slate-100 text-slate-600")}>
                      {moduleCounts[m.key]}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Item count + clear filters bar ──────────────────────────────── */}
      <div className="flex items-center gap-3">
        <span className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-500" data-testid="live-ops-item-count">
          {loading ? "Loading…" : `${filteredCards.length.toLocaleString()} item${filteredCards.length !== 1 ? "s" : ""}`}
        </span>
        {hasFilters && (
          <button
            onClick={() => { setResponsibilityFilter("all"); setUrgentFilter(false); setOverdueFilter(false); setModuleFilter("all"); setSearchQuery(""); }}
            data-testid="clear-filters-btn"
            className="text-xs font-medium text-[#2F80ED] hover:underline whitespace-nowrap"
          >
            Clear filters
          </button>
        )}
        <div className="flex-1 h-px bg-slate-200" />
      </div>

      {/* ── Level 4: Responsibility sections ───────────────────────────── */}
      {loading ? (
        <div className="space-y-8">
          {[...Array(2)].map((_, i) => (
            <div key={i}>
              <div className="h-14 bg-slate-100 rounded-xl mb-4 animate-pulse" />
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                {[...Array(4)].map((_, j) => (
                  <div key={j} className="bg-white border border-slate-200 rounded-xl h-52 animate-pulse" />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : filteredCards.length === 0 ? (
        <div><EmptyState hasFilters={hasFilters} /></div>
      ) : (
        <div className="space-y-8" data-testid="live-ops-card-grid">
          {RESPONSIBILITY_SECTIONS.map((section) => {
            const sCards = responsibilitySections[section.key] || [];
            if (sCards.length === 0) return null;
            // When one specific responsibility is selected, only show that section
            if (responsibilityFilter !== "all" && responsibilityFilter !== section.key) return null;
            const collapsed = !!sectionCollapsed[section.key];
            // Suppress section header when a specific responsibility is already selected —
            // the header is redundant when the queue shows only one responsibility group.
            const showHeader = responsibilityFilter === "all";

            return (
              <div
                key={section.key}
                ref={(el) => { sectionRefs.current[section.key] = el; }}
                data-testid={section.testid}
              >
                {showHeader && (
                  <ResponsibilitySectionHeader
                    section={section}
                    count={sCards.length}
                    collapsed={collapsed}
                    onToggle={() => toggleSection(section.key)}
                  />
                )}
                {(!showHeader || !collapsed) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                    {sCards.map((card) => (
                      <LiveOpsCard
                        key={card.id}
                        card={card}
                        onRefresh={() => load(true)}
                        staffList={staffList}
                        isManagement={isManagement}
                        onAssigned={handleAssigned}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

